"use client"

import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../Icon"
import BankCombobox from "../../BankCombobox"
import AdminUserProfileModal from "../../admin/AdminUserProfileModal"
import AdminGroupsModuleSection from "./AdminGroupsModuleSection"
import AdminPayScoreModuleSection from "./AdminPayScoreModuleSection"
import AdminUtilitiesModuleSection from "./AdminUtilitiesModuleSection"
import { formatMoney, formatDateWithTime, formatDateInputValue, formatTimestamp, getNairobiMonth, getNairobiYear, getMonthName } from "../../../utils/format"
import api from "../../../utils/api"
import { fetchAdminCompanies } from "../../../utils/adminCompanies"
import { doNamesReluctantlyMatch, maskKraPin } from "../../../utils/kraNameMatch"
import { SkeletonList } from "../../Skeleton"
import {
  buildRequestFingerprint,
  clearStableRequestKey,
  getStableRequestKey,
} from "../../../utils/requestKey"

const renderUserKraSummary = (user) => {
  const kraPin = String(user?.kraPin || "").trim()
  const kraTaxpayerName = String(user?.kraTaxpayerName || "").trim()
  const kraNameMatches = doNamesReluctantlyMatch(user?.name || "", kraTaxpayerName)

  if (!kraPin) {
    return null
  }

  return {
    toneClassName: kraNameMatches ? "text-emerald-700" : "text-red-600",
    text: kraNameMatches ? `KRA PIN: ${maskKraPin(kraPin)} • Match` : `KRA PIN: ${maskKraPin(kraPin)} • No match`,
  }
}

function AdminDashboardView({ user }) {
  const navigate = useNavigate()
  const tenantPayRentStorageKey = "frp.showTenantPayRent"
  const [users, setUsers] = useState([])
  const [displayedUsers, setDisplayedUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [showMoreCount, setShowMoreCount] = useState(3) // Start with 3 users
  const [usersLoading, setUsersLoading] = useState(false)
  // Per-user metrics
  const [userWalletBalancesById, setUserWalletBalancesById] = useState({}) // { [userId]: number }
  const [userBalanceDueById, setUserBalanceDueById] = useState({}) // { [userId]: number }
  const [userActiveTenantById, setUserActiveTenantById] = useState({}) // { [userId]: boolean } true=active tenant on a current unit, false=only former units
  const [activeUsersSort, setActiveUsersSort] = useState(null) // null | { type: 'wallet' | 'due', direction: 'desc' | 'asc' }
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [usersCollapsed, setUsersCollapsed] = useState(true)
  const [forRentCollapsed, setForRentCollapsed] = useState(true)
  const [companiesCollapsed, setCompaniesCollapsed] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(true)
  // Edit Percentage modal state
  const [showEditPercentageModal, setShowEditPercentageModal] = useState(false)
  const [editPercentageValue, setEditPercentageValue] = useState('')
  const [editTarget, setEditTarget] = useState(null) // 'engineering' | 'marketing'
  const [platformSplit, setPlatformSplit] = useState({ engineeringWalletPercentage: 0, marketingWalletPercentage: 0, platformFeePercentage: 1 })
  const [rlShareValue, setRlShareValue] = useState('')
  const [loadingSplit, setLoadingSplit] = useState(false)
  const [savingSplit, setSavingSplit] = useState(false)
  const [bankDetails, setBankDetails] = useState({ engineeringBankName: '', engineeringBankAccountNumber: '', marketingBankName: '', marketingBankAccountNumber: '', platformBankName: '', platformBankAccountNumber: '' })
  const [bankDetailsPaybill, setBankDetailsPaybill] = useState({ engineeringPaybill: '', marketingPaybill: '', platformPaybill: '' })
  const [banksCatalog, setBanksCatalog] = useState([]) // [{ name, paybill }]
  const canEditSplits = user?.role === 'A'
  const [showTenantPayRent, setShowTenantPayRent] = useState(false)

  // FRP Wallet transactions states
  const [showRLWalletTransactionsModal, setShowRLWalletTransactionsModal] = useState(false)
  const [rlWalletTransactions, setRLWalletTransactions] = useState([])
  const [rlWalletTransactionsLoading, setRLWalletTransactionsLoading] = useState(false)
  const [rlWalletStartDate, setRLWalletStartDate] = useState('')
  const [rlWalletEndDate, setRLWalletEndDate] = useState('')
  const [walletModalTitle, setWalletModalTitle] = useState('FRP Wallet Transactions')
  const [walletModalBalance, setWalletModalBalance] = useState(0)
  const [walletKey, setWalletKey] = useState('platform') // 'platform' | 'engineering' | 'marketing'
  const [rlWalletFilterOpen, setRlWalletFilterOpen] = useState(false)
  const [rlWalletSort, setRlWalletSort] = useState(null)
  const [rlWalletAcSearch, setRlWalletAcSearch] = useState('')
  const [rlWalletTransIdSearch, setRlWalletTransIdSearch] = useState('')
  const [showRLWalletDeleteConfirm, setShowRLWalletDeleteConfirm] = useState(false)
  const [rlWalletTxToDelete, setRlWalletTxToDelete] = useState(null)
  const [rlWalletDeleting, setRlWalletDeleting] = useState(false)
  const [rlWalletDeleteError, setRlWalletDeleteError] = useState('')

  // Admin wallet modal
  const [showAdminWalletModal, setShowAdminWalletModal] = useState(false)
  const [adminWalletBalance, setAdminWalletBalance] = useState(0)
  const [adminWalletTransactions, setAdminWalletTransactions] = useState([])
  const [adminWalletTransactionsLoading, setAdminWalletTransactionsLoading] = useState(false)

  // All Wallets Balances modal state
  const [showAllWalletsModal, setShowAllWalletsModal] = useState(false)
  const [allWallets, setAllWallets] = useState([]) // [{ id, name, balance, type: 'System'|'User' }]
  const [allWalletsLoading, setAllWalletsLoading] = useState(false)
  const [allWalletsSort, setAllWalletsSort] = useState('desc') // 'desc' | 'asc'
  const [showFrpDisburseModal, setShowFrpDisburseModal] = useState(false)
  const [frpDisburseMode, setFrpDisburseMode] = useState('single') // 'single' | 'bulk'
  const [frpDisburseSelectedIds, setFrpDisburseSelectedIds] = useState([])
  const [frpDisburseSearch, setFrpDisburseSearch] = useState('')
  const [frpDisburseIncludeSystemWallets, setFrpDisburseIncludeSystemWallets] = useState(false)
  const [frpDisburseSubmitting, setFrpDisburseSubmitting] = useState(false)
  const [frpDisburseStartedAt, setFrpDisburseStartedAt] = useState(null)
  const [frpDisburseBalancesLoading, setFrpDisburseBalancesLoading] = useState(false)
  const [standardDisburseSubmitting, setStandardDisburseSubmitting] = useState(false)
  const [standardDisburseStartedAt, setStandardDisburseStartedAt] = useState(null)
  const [disburseNow, setDisburseNow] = useState(Date.now())

  // Wallet labels and live balances
  const walletLabels = {
    platform: 'FRP Wallet',
    marketing: 'Marketing Wallet',
    engineering: 'Engineering Wallet'
  }
  const [walletBalances, setWalletBalances] = useState({ platform: 0, marketing: 0, engineering: 0 })
  const [systemAutoDisburse, setSystemAutoDisburse] = useState({ platform: false, engineering: false, marketing: false })
  const [rlSummary, setRlSummary] = useState({ grossRevenue: 0, disbursed: 0, rlRevenue: 0, transactions: 0 })
  const [allUsersWalletsSum, setAllUsersWalletsSum] = useState(0)
  const [, setPlatformDisbursedTotal] = useState(0)

  // Track in-flight fetches to avoid duplicate requests
  const inFlightWalletIdsRef = useRef(new Set())
  const inFlightDueIdsRef = useRef(new Set())
  // Guard initial summary fetch (prevents double-run in React 18 StrictMode dev)
  const initSummaryRef = useRef(false)

  // Summary Month/Year selector state (Africa/Nairobi time)
  const [selectedSummaryMonth, setSelectedSummaryMonth] = useState(getNairobiMonth())
  const [selectedSummaryYear, setSelectedSummaryYear] = useState(getNairobiYear())
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(false)

  // User wallet modal states
  const [showUserWalletTransactionsModal, setShowUserWalletTransactionsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userWalletTransactions, setUserWalletTransactions] = useState([])
  const [userWalletTransactionsLoading, setUserWalletTransactionsLoading] = useState(false)
  const [userWalletStartDate, setUserWalletStartDate] = useState('')
  const [userWalletEndDate, setUserWalletEndDate] = useState('')
  const [userWalletBalance, setUserWalletBalance] = useState(0)
  const [userWalletFilterOpen, setUserWalletFilterOpen] = useState(false)
  const [userWalletId, setUserWalletId] = useState(null)
  const [showUserWalletDeleteConfirm, setShowUserWalletDeleteConfirm] = useState(false)
  const [userWalletTxToDelete, setUserWalletTxToDelete] = useState(null)
  const [userWalletDeleting, setUserWalletDeleting] = useState(false)
  const [userWalletDeleteError, setUserWalletDeleteError] = useState('')

  // Mobile actions sheet state (for user actions on phones)
  const [mobileActionsUser, setMobileActionsUser] = useState(null)
  const [editingUser, setEditingUser] = useState(null)

  // Company/Property detail modals
  const [showCompanyModal, setShowCompanyModal] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [showPropertyModal, setShowPropertyModal] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState(null)

  // Helper: current month date range in YYYY-MM-DD (Africa/Nairobi time)
  const getCurrentMonthRange = () => {
    const y = getNairobiYear()
    const m = getNairobiMonth()
    const start = new Date(y, m - 1, 1)
    const end = new Date(y, m, 0)
    return { start: formatDateInputValue(start), end: formatDateInputValue(end) }
  }

  useEffect(() => {
    if (!standardDisburseSubmitting && !frpDisburseSubmitting) return undefined
    const timer = window.setInterval(() => setDisburseNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [standardDisburseSubmitting, frpDisburseSubmitting])

  // Helper: selected month/year range in YYYY-MM-DD (Africa/Nairobi time)
  const getSelectedMonthRange = () => {
    const y = Number(selectedSummaryYear)
    const mIndex = Number(selectedSummaryMonth) - 1
    const start = new Date(y, mIndex, 1)
    const end = new Date(y, mIndex + 1, 0)
    return { start: formatDateInputValue(start), end: formatDateInputValue(end) }
  }

  const getSelectedPeriodLabel = () => {
    return `${getMonthName(selectedSummaryMonth)} ${selectedSummaryYear}`
  }

  useEffect(() => {
    try {
      const stored = localStorage.getItem(tenantPayRentStorageKey)
      if (stored === "true") setShowTenantPayRent(true)
    } catch (e) {
      // Ignore storage errors and keep default off
    }
  }, [])

  const toggleTenantPayRentVisibility = () => {
    setShowTenantPayRent((prev) => {
      const next = !prev
      try {
        localStorage.setItem(tenantPayRentStorageKey, String(next))
      } catch (e) {
        // Ignore storage errors
      }
      return next
    })
  }


  const fetchWalletBalances = async () => {
    try {
      const res = await api.get('/frp/platform-wallet')
      const rlBalance = Number(res?.data?.balance || 0) // RL platform wallet balance
      const marketingBalance = Number(res?.data?.marketing?.balance || 0)
      const engineeringBalance = Number(res?.data?.engineering?.balance || 0)
      setWalletBalances({ platform: rlBalance, marketing: marketingBalance, engineering: engineeringBalance })
      setSystemAutoDisburse({
        platform: !!res?.data?.platform?.autoDisburse,
        engineering: !!res?.data?.engineering?.autoDisburse,
        marketing: !!res?.data?.marketing?.autoDisburse
      })
      // Keep separate RL summary metrics period-based; here only record platform disbursed total if needed elsewhere
      const disbursedAll = Number(res?.data?.disbursements?.totalAmount || 0)
      setPlatformDisbursedTotal(disbursedAll)
    } catch (e) {
      console.error('Failed to fetch wallet balances', e)
    }
  }

  // Fetch users data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true)
        const res = await api.get('/auth/users')
        const list = Array.isArray(res?.data?.users) ? res.data.users : []
        setUsers(list)
        // Prefetch metrics for initially displayed users (dues only; avoid wallet fetches on load)
        const initialIds = list.slice(0, showMoreCount).map(u => u?._id).filter(Boolean)
        prefetchUserWalletsAndDues(initialIds, { fetchWallet: false, fetchDue: true })
      } catch (e) {
        console.error('Failed to fetch users', e)
      } finally {
        setUsersLoading(false)
      }
    }
    fetchUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchBulkInvoiceSmsSettings()
  }, [])

  // Compute period-based RL Revenue Summary metrics (Gross, Disbursed, RL Revenue, All Wallets Balance)
  const fetchRlSummaryMetrics = async () => {
    try {
      setMonthlySummaryLoading(true)
      const { start, end } = getSelectedMonthRange()

      const [allTxRes, platformTxRes, platformSummaryRes] = await Promise.allSettled([
        api.get(`/frp/admin/all-transactions?startDate=${start}&endDate=${end}`),
        api.get(`/frp/platform-wallet/transactions?wallet=platform&startDate=${start}&endDate=${end}`),
        api.get(`/frp/platform-wallet?year=${selectedSummaryYear}&month=${String(selectedSummaryMonth).padStart(2, '0')}`)
      ])

      // From all-app transactions: Disbursed and All Wallets Balance as of end
      let grossRevenue = 0 // set from wallet summary below
      let disbursed = 0
      let allBalance = 0
      if (allTxRes.status === 'fulfilled') {
        const txs = Array.isArray(allTxRes.value?.data?.transactions) ? allTxRes.value.data.transactions : []
        for (const t of txs) {
          const amt = Number(t?.amount || 0)
          if (t?.type === 'disbursement') disbursed += Math.abs(amt)
        }
        allBalance = Number(allTxRes.value?.data?.balance || 0)
      }

      // From platform wallet summary:
      // - monthly gross revenue (from system wallet gross_revenue_YYYY_MM)
      // - live balances for platform/engineering/marketing
      // - split percentages and bank details
      if (platformSummaryRes.status === 'fulfilled') {
        const pdata = platformSummaryRes.value?.data || {}
        grossRevenue = Number(pdata?.grossRevenueThisMonth || 0)
        // Update live balances
        const platBal = Number(pdata?.platform?.balance ?? pdata?.balance ?? 0)
        const engBal = Number(pdata?.engineering?.balance ?? 0)
        const mktBal = Number(pdata?.marketing?.balance ?? 0)
        setWalletBalances({ platform: platBal, marketing: mktBal, engineering: engBal })
        // Sync system wallet manual disburse toggles
        setSystemAutoDisburse({
          platform: !!pdata?.platform?.autoDisburse,
          engineering: !!pdata?.engineering?.autoDisburse,
          marketing: !!pdata?.marketing?.autoDisburse
        })
        // Update disbursed total (for completeness)
        const disbursedAll = Number(pdata?.disbursements?.totalAmount || 0)
        setPlatformDisbursedTotal(disbursedAll)
        // Update split percentages
        const split = pdata?.splitPercentages || {}
        setPlatformSplit({
          engineeringWalletPercentage: Number(split.engineeringWalletPercentage || 0),
          marketingWalletPercentage: Number(split.marketingWalletPercentage || 0),
          platformFeePercentage: Number(split.platformFeePercentage ?? 1)
        })
        // Update bank details if available
        setBankDetails(prev => ({
          ...prev,
          platformBankName: pdata?.platform?.bankName || '',
          platformBankAccountNumber: pdata?.platform?.bankAccountNumber || '',
          engineeringBankName: pdata?.engineering?.bankName || '',
          engineeringBankAccountNumber: pdata?.engineering?.bankAccountNumber || '',
          marketingBankName: pdata?.marketing?.bankName || '',
          marketingBankAccountNumber: pdata?.marketing?.bankAccountNumber || ''
        }))
        setBankDetailsPaybill(prev => ({
          ...prev,
          platformPaybill: pdata?.platform?.paybill || '',
          engineeringPaybill: pdata?.engineering?.paybill || '',
          marketingPaybill: pdata?.marketing?.paybill || ''
        }))
      } else {
        grossRevenue = 0
      }

      // From platform wallet transactions: RL Revenue = sum of positive platform fees/payments in period
      // (match logic used in rlPeriodTotals for consistency; disbursements do NOT reduce this figure)
      let rlRevenue = 0
      if (platformTxRes.status === 'fulfilled') {
        const txs = Array.isArray(platformTxRes.value?.data?.transactions) ? platformTxRes.value.data.transactions : []
        for (const t of txs) {
          const amt = Number(t?.amount || 0)
          if ((t?.type === 'platform_fee' || t?.type === 'payment') && amt > 0) {
            rlRevenue += amt
          }
        }
      }


      setRlSummary(prev => ({ ...prev, grossRevenue, disbursed, rlRevenue }))
      setAllUsersWalletsSum(allBalance)
    } catch (e) {
      console.error('Failed to compute RL summary metrics', e)
    } finally {
      setMonthlySummaryLoading(false)
    }
  }

  // Initialize RL summary for current month on mount
  useEffect(() => {
    if (initSummaryRef.current) return
    initSummaryRef.current = true
    fetchRlSummaryMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  // Prefetch wallet balances and balance due for a set of user IDs
  const prefetchUserWalletsAndDues = async (userIds = [], options = { fetchWallet: true, fetchDue: true }) => {
    try {
      const { fetchWallet = true, fetchDue = true } = options || {}
      const ids = (Array.isArray(userIds) ? userIds : []).filter(Boolean)
      if (ids.length === 0) return
      const tasks = []
      for (const id of ids) {
        if (fetchDue && userBalanceDueById[id] === undefined && !inFlightDueIdsRef.current.has(id)) {
          inFlightDueIdsRef.current.add(id)
          tasks.push(
            api.get(`/frp/admin/user-balance-due/${id}`)
              .then(res => {
                const due = Number(res?.data?.totalBalanceDue || 0)
                setUserBalanceDueById(prev => ({ ...prev, [id]: due }))
                const units = Array.isArray(res?.data?.units) ? res.data.units : []
                const isActiveTenant = units.some(u => u?.isCurrentTenant === true)
                setUserActiveTenantById(prev => ({ ...prev, [id]: isActiveTenant }))
              })
              .catch(() => {
                setUserBalanceDueById(prev => ({ ...prev, [id]: 0 }))
              })
              .finally(() => {
                inFlightDueIdsRef.current.delete(id)
              })
          )
        }
        if (fetchWallet && userWalletBalancesById[id] === undefined && !inFlightWalletIdsRef.current.has(id)) {
          inFlightWalletIdsRef.current.add(id)
          tasks.push(
            api.get(`/wallet/user-wallet/${id}`)
              .then(res => {
                const bal = Number(res?.data?.wallet?.balance || 0)
                setUserWalletBalancesById(prev => ({ ...prev, [id]: bal }))
              })
              .catch(() => {
                setUserWalletBalancesById(prev => ({ ...prev, [id]: 0 }))
              })
              .finally(() => {
                inFlightWalletIdsRef.current.delete(id)
              })
          )
        }
      }
      if (tasks.length > 0) await Promise.allSettled(tasks)
    } catch (e) {
      // silent
    }
  }

  // System wallet transactions (real API)
  const fetchSystemWalletTransactions = async (key, startDate = '', endDate = '') => {
    try {
      setRLWalletTransactionsLoading(true)
      let url = `/frp/platform-wallet/transactions?wallet=${key}`
      const params = new URLSearchParams()
      const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange()
      const effectiveStart = startDate || defaultStart
      const effectiveEnd = endDate || defaultEnd
      if (effectiveStart) params.append('startDate', effectiveStart)
      if (effectiveEnd) params.append('endDate', effectiveEnd)
      if (params.toString()) url += `&${params.toString()}`

      const res = await api.get(url)
      setWalletModalBalance(res?.data?.balance || 0)
      setRLWalletTransactions(res?.data?.transactions || [])
    } catch (error) {
      console.error('Error fetching wallet transactions:', error)
      alert('Error fetching wallet transactions: ' + (error.response?.data?.error || error.message))
    } finally {
      setRLWalletTransactionsLoading(false)
    }
  }

  // All-app consolidated transactions (admin only)
  const fetchAllAppTransactions = async (startDate = '', endDate = '') => {
    try {
      setRLWalletTransactionsLoading(true)
      let url = `/frp/admin/all-transactions`
      const params = new URLSearchParams()
      const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange()
      const effectiveStart = startDate || defaultStart
      const effectiveEnd = endDate || defaultEnd
      if (effectiveStart) params.append('startDate', effectiveStart)
      if (effectiveEnd) params.append('endDate', effectiveEnd)
      if (params.toString()) url += `?${params.toString()}`

      const res = await api.get(url)
      setWalletModalBalance(res?.data?.balance || 0)
      setRLWalletTransactions(Array.isArray(res?.data?.transactions) ? res.data.transactions : [])
    } catch (error) {
      console.error('Error fetching all-app transactions:', error)
      alert('Error fetching transactions: ' + (error.response?.data?.error || error.message))
    } finally {
      setRLWalletTransactionsLoading(false)
    }
  }

  // Wallet transactions functions
  const fetchRLWalletTransactions = async (startDate = '', endDate = '') => {
    if (walletKey === 'all') {
      await fetchAllAppTransactions(startDate, endDate)
    } else {
      await fetchSystemWalletTransactions(walletKey, startDate, endDate)
    }
  }

  const fetchFilteredRLWalletTransactions = () => {
    fetchRLWalletTransactions(rlWalletStartDate, rlWalletEndDate)
  }

  const clearRLWalletDateFilters = () => {
    setRLWalletStartDate('')
    setRLWalletEndDate('')
    fetchRLWalletTransactions()
  }

  const fetchAdminWalletTransactions = async () => {
    try {
      setAdminWalletTransactionsLoading(true)
      const res = await api.get('/wallet/admin-wallet/transactions')
      setAdminWalletBalance(Number(res?.data?.balance || 0))
      setAdminWalletTransactions(Array.isArray(res?.data?.transactions) ? res.data.transactions : [])
    } catch (e) {
      console.error('Failed to fetch admin wallet transactions', e)
      alert(e?.response?.data?.error || 'Failed to fetch admin wallet transactions')
    } finally {
      setAdminWalletTransactionsLoading(false)
    }
  }

  const openAdminWalletModal = async () => {
    setShowAdminWalletModal(true)
    await fetchAdminWalletTransactions()
  }

  const openRLWalletDeleteConfirm = (tx) => {
    setRlWalletTxToDelete(tx || null)
    setRlWalletDeleteError('')
    setShowRLWalletDeleteConfirm(true)
  }

  const closeRLWalletDeleteConfirm = () => {
    if (rlWalletDeleting) return
    setShowRLWalletDeleteConfirm(false)
    setRlWalletTxToDelete(null)
    setRlWalletDeleteError('')
  }

  const confirmDeleteRLWalletTransaction = async () => {
    if (!rlWalletTxToDelete?._id) return
    try {
      setRlWalletDeleting(true)
      setRlWalletDeleteError('')
      const config = rlWalletTxToDelete?.walletId ? { params: { walletId: rlWalletTxToDelete.walletId } } : undefined
      await api.delete(`/frp/admin/wallet-transactions/${rlWalletTxToDelete._id}`, config)
      setShowRLWalletDeleteConfirm(false)
      setRlWalletTxToDelete(null)
      await fetchRLWalletTransactions(rlWalletStartDate, rlWalletEndDate)
      fetchWalletBalances()
    } catch (e) {
      console.error('Failed to delete wallet transaction', e)
      setRlWalletDeleteError(e?.response?.data?.error || e?.message || 'Failed to delete transaction')
    } finally {
      setRlWalletDeleting(false)
    }
  }

  // Open All Wallets Balances modal and load data (users + system wallets)
  const openAllWalletsBalancesModal = async () => {
    try {
      setAllWalletsLoading(true)
      const systemWallets = [
        { id: 'system:platform', name: walletLabels.platform, balance: Number(walletBalances.platform || 0), type: 'System' },
        { id: 'system:marketing', name: walletLabels.marketing, balance: Number(walletBalances.marketing || 0), type: 'System' },
        { id: 'system:engineering', name: walletLabels.engineering, balance: Number(walletBalances.engineering || 0), type: 'System' }
      ]

      const ids = (Array.isArray(users) ? users : []).map(u => u?._id).filter(Boolean)
      let userWallets = []
      try {
        const resp = await api.post('/wallet/admin/user-balances', { userIds: ids })
        userWallets = Array.isArray(resp?.data?.items)
          ? resp.data.items.map(it => ({
            id: it.id,
            name: it.name || 'User',
            balance: Number(it.balance || 0),
            type: 'User'
          }))
          : []
      } catch (err) {
        console.warn('Batch balances failed, falling back to limited concurrency:', err?.message || err)
        // Fallback: limited concurrency (8 at a time)
        const CONCURRENCY = 8
        const queue = [...ids]
        const results = []
        const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
          while (queue.length) {
            const id = queue.shift()
            try {
              const r = await api.get(`/wallet/user-wallet/${id}`)
              const bal = Number(r?.data?.wallet?.balance || 0)
              const u = users.find(x => String(x._id) === String(id))
              results.push({ id, name: u?.name || 'User', balance: bal, type: 'User' })
            } catch {
              const u = users.find(x => String(x._id) === String(id))
              results.push({ id, name: u?.name || 'User', balance: 0, type: 'User' })
            }
          }
        })
        await Promise.allSettled(workers)
        userWallets = results
      }
      setAllWallets([...systemWallets, ...userWallets])
      setShowAllWalletsModal(true)
    } catch (e) {
      console.error('Failed to load all wallets list', e)
      alert('Failed to load wallets list')
    } finally {
      setAllWalletsLoading(false)
    }
  }

  const sortedAllWallets = (() => {
    const list = Array.isArray(allWallets) ? [...allWallets] : []
    if (allWalletsSort === 'asc') return list.sort((a, b) => (a.balance - b.balance))
    return list.sort((a, b) => (b.balance - a.balance))
  })()

  const eligibleFrpDisbursementUsers = getEligibleFrpDisbursementUsers().filter((oneUser) => Number(userWalletBalancesById[oneUser._id] || 0) > 0)
  const filteredFrpDisbursementUsers = eligibleFrpDisbursementUsers.filter((oneUser) => {
    const q = frpDisburseSearch.trim().toLowerCase()
    if (!q) return true
    return [
      oneUser?.name,
      oneUser?.phone,
      oneUser?.bankName,
      oneUser?.bankAccNumber,
      oneUser?.paybill,
      String(Number(userWalletBalancesById[oneUser._id] || 0))
    ].some((value) => String(value || '').toLowerCase().includes(q))
  })
  const selectedFrpUsers = getEligibleFrpDisbursementUsers().filter((oneUser) => frpDisburseSelectedIds.includes(oneUser._id))
  const selectedFrpTotal = selectedFrpUsers.reduce((sum, oneUser) => sum + Number(userWalletBalancesById[oneUser._id] || 0), 0)
  const standardDisburseElapsedSeconds = standardDisburseStartedAt
    ? Math.max(1, Math.floor((disburseNow - standardDisburseStartedAt) / 1000))
    : 0
  const frpDisburseElapsedSeconds = frpDisburseStartedAt
    ? Math.max(1, Math.floor((disburseNow - frpDisburseStartedAt) / 1000))
    : 0

  const handleDisburse = async () => {
    try {
      if (!window.confirm('Proceed with standard disbursement?')) return
      setStandardDisburseSubmitting(true)
      setStandardDisburseStartedAt(Date.now())
      const res = await api.post('/frp/disburse')
      alert(res?.data?.message || 'Disbursement completed.')
      // Refresh balances and recompute summaries
      await fetchRlSummaryMetrics()
      // If RL transactions modal is open, refresh it to show new disbursement entries
      if (showRLWalletTransactionsModal) {
        const { start, end } = getCurrentMonthRange()
        await fetchSystemWalletTransactions(walletKey || 'platform', start, end)
      }
    } catch (e) {
      console.error('Disbursement failed', e)
      alert(e?.response?.data?.error || 'Disbursement failed or is not available yet.')
    } finally {
      setStandardDisburseSubmitting(false)
      setStandardDisburseStartedAt(null)
    }
  }

  function getEligibleFrpDisbursementUsers() {
    return (Array.isArray(users) ? users : []).filter((oneUser) => {
      const hasRole = oneUser?.role === 'M' || oneUser?.role === 'L' || (Array.isArray(oneUser?.propertyRoles) && oneUser.propertyRoles.some((pr) => pr?.role === 'M' || pr?.role === 'L'))
      const hasBanking = Boolean(oneUser?.bankAccNumber && String(oneUser.bankAccNumber).trim() && oneUser?.paybill && String(oneUser.paybill).trim())
      return Boolean(oneUser?._id) && !!oneUser?.autoDisburse && hasRole && hasBanking
    })
  }

  const preloadFrpDisbursementBalances = async (baseUsers = []) => {
    const ids = (Array.isArray(baseUsers) ? baseUsers : []).map((oneUser) => oneUser?._id).filter(Boolean)
    if (!ids.length) return
    try {
      setFrpDisburseBalancesLoading(true)
      const res = await api.post('/wallet/admin/user-balances', { userIds: ids })
      const items = Array.isArray(res?.data?.items) ? res.data.items : []
      if (items.length) {
        setUserWalletBalancesById((prev) => {
          const next = { ...prev }
          for (const item of items) {
            if (item?.id) next[item.id] = Number(item?.balance || 0)
          }
          return next
        })
      }
    } catch (e) {
      console.error('Failed to preload FRP disbursement balances', e)
    } finally {
      setFrpDisburseBalancesLoading(false)
    }
  }

  const openFrpDisburseModal = () => {
    const baseEligibleUsers = getEligibleFrpDisbursementUsers()
    if (!baseEligibleUsers.length) {
      alert('No eligible users found. Make sure at least one Manager/Landlord has auto-disburse enabled with bank details and paybill.')
      return
    }
    setFrpDisburseMode('single')
    setFrpDisburseSearch('')
    setFrpDisburseIncludeSystemWallets(false)
    setFrpDisburseSelectedIds([])
    setShowFrpDisburseModal(true)
    preloadFrpDisbursementBalances(baseEligibleUsers)
  }

  const closeFrpDisburseModal = () => {
    setShowFrpDisburseModal(false)
    setFrpDisburseSubmitting(false)
    setFrpDisburseStartedAt(null)
  }

  const toggleFrpDisburseUser = (userId) => {
    if (!userId) return
    if (frpDisburseMode === 'single') {
      setFrpDisburseSelectedIds([userId])
      return
    }
    setFrpDisburseSelectedIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ))
  }

  const handleSubmitFrpDisbursement = async () => {
    try {
      if (!frpDisburseSelectedIds.length) {
        alert('Select at least one user to disburse via FRP.')
        return
      }
      const selectedUsers = getEligibleFrpDisbursementUsers().filter((oneUser) => frpDisburseSelectedIds.includes(oneUser._id))
      const selectedLabel = frpDisburseMode === 'single'
        ? (selectedUsers[0]?.name || 'selected user')
        : `${selectedUsers.length} selected users`
      if (!window.confirm(`Proceed with FRP proxy disbursement for ${selectedLabel}?`)) return
      setFrpDisburseSubmitting(true)
      setFrpDisburseStartedAt(Date.now())
      const res = await api.post('/frp/disburse-via-frp', {
        userIds: frpDisburseSelectedIds,
        includeSystemWallets: frpDisburseIncludeSystemWallets
      })
      alert(res?.data?.message || 'FRP proxy disbursement completed.')
      closeFrpDisburseModal()
      await fetchRlSummaryMetrics()
      await fetchWalletBalances()
      if (showRLWalletTransactionsModal) {
        const { start, end } = getCurrentMonthRange()
        await fetchSystemWalletTransactions(walletKey || 'platform', start, end)
      }
    } catch (e) {
      console.error('FRP proxy disbursement failed', e)
      alert(e?.response?.data?.error || 'FRP proxy disbursement failed or is not available yet.')
    } finally {
      setFrpDisburseSubmitting(false)
      setFrpDisburseStartedAt(null)
    }
  }

  // Platform split functions
  const fetchPlatformSplit = async () => {
    try {
      setLoadingSplit(true)
      const res = await api.get('/frp/platform-wallet')
      const split = res?.data?.splitPercentages || {}
      setPlatformSplit({
        engineeringWalletPercentage: Number(split.engineeringWalletPercentage || 0),
        marketingWalletPercentage: Number(split.marketingWalletPercentage || 0),
        platformFeePercentage: Number(split.platformFeePercentage ?? 1)
      })
      // Prefill bank details if available
      setBankDetails(prev => ({
        ...prev,
        platformBankName: res?.data?.platform?.bankName || '',
        platformBankAccountNumber: res?.data?.platform?.bankAccountNumber || '',
        engineeringBankName: res?.data?.engineering?.bankName || '',
        engineeringBankAccountNumber: res?.data?.engineering?.bankAccountNumber || '',
        marketingBankName: res?.data?.marketing?.bankName || '',
        marketingBankAccountNumber: res?.data?.marketing?.bankAccountNumber || ''
      }))
      setBankDetailsPaybill(prev => ({
        ...prev,
        platformPaybill: res?.data?.platform?.paybill || '',
        engineeringPaybill: res?.data?.engineering?.paybill || '',
        marketingPaybill: res?.data?.marketing?.paybill || ''
      }))
      setSystemAutoDisburse({
        platform: !!res?.data?.platform?.autoDisburse,
        engineering: !!res?.data?.engineering?.autoDisburse,
        marketing: !!res?.data?.marketing?.autoDisburse
      })
    } catch (e) {
      console.error('Failed to fetch platform split', e)
    } finally {
      setLoadingSplit(false)
    }
  }

  const toggleSystemWalletAutoDisburse = async (key, next) => {
    try {
      if (next === true) {
        const bankName = key === 'platform' ? bankDetails.platformBankName : key === 'engineering' ? bankDetails.engineeringBankName : bankDetails.marketingBankName
        const bankAcc = key === 'platform' ? bankDetails.platformBankAccountNumber : key === 'engineering' ? bankDetails.engineeringBankAccountNumber : bankDetails.marketingBankAccountNumber
        const paybill = key === 'platform' ? bankDetailsPaybill.platformPaybill : key === 'engineering' ? bankDetailsPaybill.engineeringPaybill : bankDetailsPaybill.marketingPaybill
        if (!bankName || !bankName.trim() || !bankAcc || !bankAcc.trim() || !paybill || !String(paybill).trim()) {
          alert('Add bank name, bank account number, and paybill before enabling manual disburse')
          return
        }
      }
      await api.put('/frp/platform-wallet/auto-disburse', { wallet: key, autoDisburse: next })
      setSystemAutoDisburse(prev => ({ ...prev, [key]: next }))
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to update manual disburse setting')
    }
  }

  // Keep RL Share editor in sync with fetched platform fee
  useEffect(() => {
    setRlShareValue(String(platformSplit.platformFeePercentage ?? ''))
  }, [platformSplit.platformFeePercentage])

  // Save handler for RL Share (%) in FRP section
  const savePlatformShare = async () => {
    const parsed = parseFloat(rlShareValue)
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      alert('Please enter a valid percentage between 0 and 100')
      return
    }
    try {
      setSavingSplit(true)
      const payload = {
        engineeringWalletPercentage: platformSplit.engineeringWalletPercentage,
        marketingWalletPercentage: platformSplit.marketingWalletPercentage,
        platformFeePercentage: parsed,
        platformBankName: bankDetails.platformBankName,
        platformBankAccountNumber: bankDetails.platformBankAccountNumber,
        platformPaybill: bankDetailsPaybill.platformPaybill,
        engineeringBankName: bankDetails.engineeringBankName,
        engineeringBankAccountNumber: bankDetails.engineeringBankAccountNumber,
        engineeringPaybill: bankDetailsPaybill.engineeringPaybill,
        marketingBankName: bankDetails.marketingBankName,
        marketingBankAccountNumber: bankDetails.marketingBankAccountNumber,
        marketingPaybill: bankDetailsPaybill.marketingPaybill
      }
      const res = await api.put('/frp/platform-wallet/settings', payload)
      const updated = res?.data?.splitPercentages
      setPlatformSplit({
        engineeringWalletPercentage: Number(updated?.engineeringWalletPercentage ?? platformSplit.engineeringWalletPercentage ?? 0),
        marketingWalletPercentage: Number(updated?.marketingWalletPercentage ?? platformSplit.marketingWalletPercentage ?? 0),
        platformFeePercentage: Number(updated?.platformFeePercentage ?? parsed)
      })
      alert('RL Share updated')
    } catch (e) {
      console.error('Failed to save RL Share', e)
      alert(e?.response?.data?.error || 'Failed to save RL Share')
    } finally {
      setSavingSplit(false)
    }
  }

  const openEditPercentage = (target) => {
    setEditTarget(target)
    const value = target === 'engineering' ? platformSplit.engineeringWalletPercentage : target === 'marketing' ? platformSplit.marketingWalletPercentage : platformSplit.platformFeePercentage
    setEditPercentageValue(String(value))
    setShowEditPercentageModal(true)
  }

  const savePlatformSplit = async () => {
    const parsed = parseFloat(editPercentageValue)
    const nextSplit = { ...platformSplit }
    // Only validate percentages when editing eng/mkt
    if (editTarget === 'engineering' || editTarget === 'marketing' || editTarget === 'platform') {
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
        alert('Please enter a valid percentage between 0 and 100')
        return
      }
      if (editTarget === 'engineering') nextSplit.engineeringWalletPercentage = parsed
      if (editTarget === 'marketing') nextSplit.marketingWalletPercentage = parsed
      if (editTarget === 'platform') nextSplit.platformFeePercentage = parsed
      if (nextSplit.engineeringWalletPercentage + nextSplit.marketingWalletPercentage > 100) {
        alert('Sum of Engineering and Marketing percentages cannot exceed 100')
        return
      }
    }
    try {
      setSavingSplit(true)
      const payload = {
        engineeringWalletPercentage: nextSplit.engineeringWalletPercentage,
        marketingWalletPercentage: nextSplit.marketingWalletPercentage,
        platformFeePercentage: nextSplit.platformFeePercentage,
        platformBankName: bankDetails.platformBankName,
        platformBankAccountNumber: bankDetails.platformBankAccountNumber,
        platformPaybill: bankDetailsPaybill.platformPaybill,
        engineeringBankName: bankDetails.engineeringBankName,
        engineeringBankAccountNumber: bankDetails.engineeringBankAccountNumber,
        engineeringPaybill: bankDetailsPaybill.engineeringPaybill,
        marketingBankName: bankDetails.marketingBankName,
        marketingBankAccountNumber: bankDetails.marketingBankAccountNumber,
        marketingPaybill: bankDetailsPaybill.marketingPaybill
      }
      const res = await api.put('/frp/platform-wallet/settings', payload)
      const updated = res?.data?.splitPercentages || nextSplit
      setPlatformSplit({
        engineeringWalletPercentage: Number(updated.engineeringWalletPercentage || 0),
        marketingWalletPercentage: Number(updated.marketingWalletPercentage || 0),
        platformFeePercentage: Number(updated.platformFeePercentage ?? nextSplit.platformFeePercentage ?? 1)
      })
      // Update bank details from response if returned
      if (res?.data?.wallets) {
        setBankDetails(prev => ({
          ...prev,
          platformBankName: res.data.wallets.platform?.bankName || '',
          platformBankAccountNumber: res.data.wallets.platform?.bankAccountNumber || '',
          engineeringBankName: res.data.wallets.engineering?.bankName || '',
          engineeringBankAccountNumber: res.data.wallets.engineering?.bankAccountNumber || '',
          marketingBankName: res.data.wallets.marketing?.bankName || '',
          marketingBankAccountNumber: res.data.wallets.marketing?.bankAccountNumber || ''
        }))
      }
      setShowEditPercentageModal(false)
      setEditTarget(null)
    } catch (e) {
      console.error('Failed to save platform split', e)
      const msg = e?.response?.data?.error || e?.message || 'Failed to save percentage settings'
      alert(msg)
    } finally {
      setSavingSplit(false)
    }
  }

  // ForRent module data (real)
  const [companies, setCompanies] = useState([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [companiesSearch, setCompaniesSearch] = useState('')
  const [properties, setProperties] = useState([])
  // eslint-disable-next-line no-unused-vars
  const [propertiesLoading, setPropertiesLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [propertiesSearch, setPropertiesSearch] = useState('')
  const [companiesShowCount, setCompaniesShowCount] = useState(5)
  // eslint-disable-next-line no-unused-vars
  const [propertiesShowCount, setPropertiesShowCount] = useState(5)
  const [expandedCompanyIds, setExpandedCompanyIds] = useState(new Set())
  const [expandedPropertyIds, setExpandedPropertyIds] = useState(new Set())
  const [unitsByPropertyId, setUnitsByPropertyId] = useState(new Map())
  const [unitsLoadingByPropertyId, setUnitsLoadingByPropertyId] = useState(new Map())
  const [unitsShowCountByPropertyId, setUnitsShowCountByPropertyId] = useState(new Map())
  const [companyActionsDropdownOpenId, setCompanyActionsDropdownOpenId] = useState(null)
  const [propertyActionsDropdownOpenId, setPropertyActionsDropdownOpenId] = useState(null)
  // Landlords and Tenants subsections
  const [landlordsCollapsed, setLandlordsCollapsed] = useState(true)
  const [tenantsCollapsed, setTenantsCollapsed] = useState(true)
  const [landlordsSearch, setLandlordsSearch] = useState('')
  const [tenantsSearch, setTenantsSearch] = useState('')
  const [landlordsShowCount, setLandlordsShowCount] = useState(5)
  const [tenantsShowCount, setTenantsShowCount] = useState(5)
  const [tenantsSort, setTenantsSort] = useState(null) // null | { type: 'wallet' | 'due', direction: 'desc' | 'asc' }
  // FRP tenants: bulk invoices
  const [bulkTenantInvoiceLoading, setBulkTenantInvoiceLoading] = useState(false)
  const [bulkInvoiceSmsEnabled, setBulkInvoiceSmsEnabled] = useState(true)
  const [bulkInvoiceSmsTemplate, setBulkInvoiceSmsTemplate] = useState(
    'RecordLOGS: Hello {{tenantName}}. Your rent invoice for {{propertyName}} {{unitLabel}} has been generated. Monthly rent: {{rentAmount}}. Balance due: {{balanceDue}}.'
  )
  // Admin: Trigger payments for all tenants
  const [triggerAllPaymentsLoading, setTriggerAllPaymentsLoading] = useState(false)
  const [triggerAllPaymentsSummary, setTriggerAllPaymentsSummary] = useState(null)

  // Withdrawal charges management
  const [showWithdrawalChargesModal, setShowWithdrawalChargesModal] = useState(false)
  const [withdrawalCharges, setWithdrawalCharges] = useState([])
  const [withdrawalChargesLoading, setWithdrawalChargesLoading] = useState(false)
  const [withdrawalChargesSaving, setWithdrawalChargesSaving] = useState(false)

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true)
      const companiesResponse = await fetchAdminCompanies()
      setCompanies(companiesResponse)
    } catch (e) {
      console.error('Failed to fetch companies', e)
      setCompanies([])
    } finally {
      setCompaniesLoading(false)
    }
  }

  const fetchBulkInvoiceSmsSettings = async () => {
    try {
      const res = await api.get('/frp/admin/bulk-invoice-notification-settings')
      const settings = res?.data?.settings || {}
      setBulkInvoiceSmsEnabled(settings?.bulkInvoiceSmsEnabled !== false)
      setBulkInvoiceSmsTemplate(
        settings?.bulkInvoiceSmsTemplate ||
        'RecordLOGS: Hello {{tenantName}}. Your rent invoice for {{propertyName}} {{unitLabel}} has been generated. Monthly rent: {{rentAmount}}. Balance due: {{balanceDue}}.'
      )
    } catch (e) {
      console.error('Failed to fetch bulk invoice notification settings', e)
      alert(e?.response?.data?.error || 'Failed to load bulk invoice SMS settings')
    }
  }

  // Admin: Bulk send invoices to ALL active tenants across all properties (or scoped later if needed)
  const handleAdminBulkSendInvoices = async () => {
    if (!window.confirm('Send invoices for all active tenants on all their assigned units?')) return
    try {
      setBulkTenantInvoiceLoading(true)
      const res = await api.post('/frp/admin/bulk-invoices', {
        sendInvoiceSms: bulkInvoiceSmsEnabled,
        invoiceSmsTemplate: bulkInvoiceSmsTemplate
      })
      const created = Number(res?.data?.summary?.invoicesCreated || 0)
      const considered = Number(res?.data?.summary?.unitsConsidered || 0)
      const results = Array.isArray(res?.data?.results) ? res.data.results : []
      const skipped = results.filter((row) => String(row?.status || '') === 'skipped').length
      const errors = results.filter((row) => String(row?.status || '') === 'error').length

      const reasonCounts = results.reduce((acc, row) => {
        const reason = String(row?.reason || '').trim()
        if (!reason) return acc
        acc[reason] = (acc[reason] || 0) + 1
        return acc
      }, {})

      const topReasonLines = Object.entries(reasonCounts)
        .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
        .slice(0, 8)
        .map(([reason, count]) => `${reason}: ${count}`)

      let message = `Bulk invoices completed.\nUnits considered: ${considered}\nCreated: ${created}\nSkipped: ${skipped}\nErrors: ${errors}`
      if (created === 0 && topReasonLines.length > 0) {
        message += `\n\nWhy 0 created:\n${topReasonLines.join('\n')}`
      }
      alert(message)
    } catch (e) {
      console.error('Admin bulk invoices error', e)
      alert(e?.response?.data?.error || 'Failed to send bulk invoices')
    } finally {
      setBulkTenantInvoiceLoading(false)
    }
  }

  // Admin: Trigger payments for ALL tenants across the system
  const handleAdminTriggerAllPayments = async () => {
    if (!window.confirm('Trigger payments from all tenant wallets with unpaid rent?')) return
    try {
      setTriggerAllPaymentsLoading(true)
      setTriggerAllPaymentsSummary(null)
      const storageKey = `recordlogs.admin-rent-batch.${user?._id || user?.id || "admin"}`
      const requestKey = getStableRequestKey({
        storageKey,
        fingerprint: buildRequestFingerprint({ scope: "all-rentals" }),
        prefix: "admin-rent-batch",
      })
      const resp = await api.post('/frp/admin/trigger-payments', {
        idempotencyKey: requestKey,
      })
      if (!resp?.data?.retryWithSameRequestKey) {
        clearStableRequestKey({ storageKey, requestKey })
      }
      setTriggerAllPaymentsSummary(resp?.data || null)
      // Refresh summaries to reflect new platform revenue/balances
      await fetchRlSummaryMetrics()
      const processed = Number(resp?.data?.summary?.paymentsProcessed || 0)
      const collected = Number(resp?.data?.summary?.totalCollected || 0)
      alert(`Triggered payments: ${processed} processed, Collected ${formatMoney(collected)}`)
    } catch (e) {
      console.error('Admin trigger payments error', e)
      alert('Error triggering payments: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setTriggerAllPaymentsLoading(false)
    }
  }

  const fetchPropertiesList = async () => {
    try {
      setPropertiesLoading(true)
      // Admin page should use admin properties endpoint for complete view
      const res = await api.get('/frp/admin/properties')
      setProperties(Array.isArray(res?.data?.properties) ? res.data.properties : [])
    } catch (e) {
      console.error('Failed to fetch properties', e)
      setProperties([])
    } finally {
      setPropertiesLoading(false)
    }
  }

  const toggleCompanyExpanded = (companyId) => {
    setExpandedCompanyIds(prev => {
      const next = new Set(prev)
      if (next.has(companyId)) next.delete(companyId)
      else next.add(companyId)
      return next
    })
  }

  const togglePropertyExpanded = async (propertyId) => {
    setExpandedPropertyIds(prev => {
      const next = new Set(prev)
      if (next.has(propertyId)) next.delete(propertyId)
      else next.add(propertyId)
      return next
    })
    // Lazy-load units if not loaded
    if (!unitsByPropertyId.has(propertyId)) {
      await fetchUnitsForProperty(propertyId)
    }
  }

  const fetchUnitsForProperty = async (propertyId) => {
    try {
      setUnitsLoadingByPropertyId(prev => new Map(prev.set(propertyId, true)))
      const res = await api.get(`/frp/admin/properties/${propertyId}/units`)
      const units = Array.isArray(res?.data?.units) ? res.data.units : []
      setUnitsByPropertyId(prev => new Map(prev.set(propertyId, units)))
      setUnitsShowCountByPropertyId(prev => new Map(prev.set(propertyId, 5)))
    } catch (e) {
      console.error('Failed to fetch units for property', propertyId, e)
      setUnitsByPropertyId(prev => new Map(prev.set(propertyId, [])))
    } finally {
      setUnitsLoadingByPropertyId(prev => new Map(prev.set(propertyId, false)))
    }
  }



  useEffect(() => {
    // Platform wallet summary call above sets balances/split/banks; avoid duplicate calls here
    // Load FRP module data
    fetchCompanies()
    fetchPropertiesList()
  }, [canEditSplits])

  // Load banks catalog from embedded xlsx via dynamic import (client bundle)
  useEffect(() => {
    let mounted = true
    const loadBanks = async () => {
      try {
        // 1) Prefer JSON list in public: /bankslist.json -> [{ name, paybill }]
        let response = await fetch('/bankslist.json')
        if (response.ok) {
          const ct = (response.headers.get('content-type') || '').toLowerCase()
          if (ct.includes('application/json')) {
            const data = await response.json()
            const list = Array.isArray(data) ? data.filter(b => b && b.name) : []
            if (mounted) setBanksCatalog(list)
            return
          }
        }
        // 2) Otherwise, try xlsx in public: /bankslist.xlsx (requires xlsx package)
        try {
          const XLSX = await import('xlsx')
          response = await fetch('/bankslist.xlsx')
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer()
            const wb = XLSX.read(arrayBuffer, { type: 'array' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 })
            const rows = Array.isArray(json) ? json : []
            const header = rows[0] || []
            const nameIdx = header.findIndex(h => String(h).toLowerCase() === 'name')
            const paybillIdx = header.findIndex(h => String(h).toLowerCase() === 'paybill')
            const list = rows.slice(1).map(r => ({ name: r[nameIdx] || '', paybill: r[paybillIdx] || '' })).filter(b => b.name)
            if (mounted) setBanksCatalog(list)
          }
        } catch (e) {
          // xlsx not installed or load failed; leave catalog empty and rely on manual entry
          console.warn('XLSX not available; skipping xlsx parsing')
        }
      } catch (e) {
        console.warn('Failed to load banks catalog', e)
      }
    }
    loadBanks()
    return () => { mounted = false }
  }, [])

  // Filter and paginate users based on search and show more count
  useEffect(() => {
    const filtered = users.filter(u => {
      const name = (u.name || '').toLowerCase()
      const email = (u.email || '').toLowerCase()
      const idNumber = (u.idNumber || '').toLowerCase()
      const objectId = (u._id ? String(u._id) : '').toLowerCase()
      const q = searchQuery.toLowerCase()
      return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
    })
    // Apply sorting
    let sorted = [...filtered]
    if (activeUsersSort) {
      if (activeUsersSort.type === 'wallet') {
        sorted.sort((a, b) => {
          const av = Number(userWalletBalancesById[a._id] ?? 0)
          const bv = Number(userWalletBalancesById[b._id] ?? 0)
          return activeUsersSort.direction === 'asc' ? (av - bv) : (bv - av)
        })
      } else if (activeUsersSort.type === 'due') {
        sorted.sort((a, b) => {
          const av = Number(userBalanceDueById[a._id] ?? 0)
          const bv = Number(userBalanceDueById[b._id] ?? 0)
          return activeUsersSort.direction === 'asc' ? (av - bv) : (bv - av)
        })
      }
    }
    const slice = sorted.slice(0, showMoreCount)
    setDisplayedUsers(slice)
  }, [users, searchQuery, showMoreCount, activeUsersSort, userWalletBalancesById, userBalanceDueById])

  // Prefetch metrics for displayed users:
  // - Always prefetch balance due and wallet balance for display
  useEffect(() => {
    const ids = (Array.isArray(displayedUsers) ? displayedUsers : []).map(u => u?._id).filter(Boolean)
    if (ids.length === 0) return
    prefetchUserWalletsAndDues(ids, { fetchWallet: true, fetchDue: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedUsers, activeUsersSort])

  // Prefetch balances for tenants when expanded.
  // If sorting by wallet (or due), fetch for ALL filtered tenants to ensure correct sorting.
  useEffect(() => {
    if (tenantsCollapsed) return
    const filtered = users
      .filter(u => getDistinctRoles(u).includes('T'))
      .filter(u => {
        const name = (u.name || '').toLowerCase()
        const email = (u.email || '').toLowerCase()
        const idNumber = (u.idNumber || '').toLowerCase()
        const objectId = (u._id ? String(u._id) : '').toLowerCase()
        const q = tenantsSearch.toLowerCase()
        return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
      })
    const scope = (tenantsSort?.type === 'wallet' || tenantsSort?.type === 'due') ? filtered : filtered.slice(0, tenantsShowCount)
    const ids = scope
      .map(u => u?._id)
      .filter(Boolean)
    if (ids.length > 0) {
      prefetchUserWalletsAndDues(ids, { fetchWallet: true, fetchDue: true })
    }
  }, [users, tenantsSearch, tenantsShowCount, tenantsCollapsed, tenantsSort])

  // Handle outside clicks for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const anyOpen = dropdownOpen || companyActionsDropdownOpenId || propertyActionsDropdownOpenId
      if (anyOpen) {
        // Check if the click is outside any dropdown
        const dropdownElements = document.querySelectorAll('[data-dropdown-container]')
        let clickedInside = false

        dropdownElements.forEach(element => {
          if (element.contains(event.target)) {
            clickedInside = true
          }
        })

        if (!clickedInside) {
          setDropdownOpen(null)
          setCompanyActionsDropdownOpenId(null)
          setPropertyActionsDropdownOpenId(null)
        }
      }
    }

    const anyOpen = dropdownOpen || companyActionsDropdownOpenId || propertyActionsDropdownOpenId
    if (anyOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [dropdownOpen, companyActionsDropdownOpenId, propertyActionsDropdownOpenId])

  const handleShowMore = () => {
    setShowMoreCount(prev => prev + 5)
  }

  const startImpersonationForUser = async (
    targetUserId,
    {
      destination = '/dashboard',
      missingTargetMessage = 'User not found.',
      failureMessage = 'Failed to impersonate user.',
      noTokenMessage = 'Failed to impersonate user: No token returned.',
    } = {}
  ) => {
    if (!targetUserId) {
      alert(missingTargetMessage)
      return false
    }

    try {
      const res = await api.post('/auth/impersonate', { userId: targetUserId })
      if (!res?.data?.token) {
        alert(noTokenMessage)
        return false
      }

      const currentToken = localStorage.getItem('token') || ''
      if (currentToken) localStorage.setItem('impersonatorToken', currentToken)
      localStorage.setItem('impersonationActive', '1')
      localStorage.setItem('impersonatorUserId', user?._id || '')
      localStorage.setItem('impersonatorName', user?.name || 'Admin')
      localStorage.setItem('impersonatedUserId', targetUserId)
      localStorage.setItem('impersonationReturnPath', '/admin')
      localStorage.setItem('token', res.data.token)

      window.dispatchEvent(new Event('impersonationChanged'))
      navigate(destination)
      return true
    } catch (e) {
      console.error('Impersonation failed', e)
      alert(e?.response?.data?.error || failureMessage)
      return false
    }
  }

  const handleActionClick = async (userId, action) => {
    setDropdownOpen(null)
    if (action === 'Wallet') {
      const target = users.find(u => String(u._id) === String(userId))
      setSelectedUser(target || null)
      // Initialize filters to current month
      const { start, end } = getCurrentMonthRange()
      setUserWalletStartDate(start)
      setUserWalletEndDate(end)
      // Fetch wallet balance then transactions
      fetchUserWalletBalanceAndTransactions(userId)
      setShowUserWalletTransactionsModal(true)
      return
    }
    if (action === 'View Account') {
      await startImpersonationForUser(userId)
      return
    }
    console.log(`Action: ${action} for user: ${userId}`)
  }

  const toggleUserAutoDisburse = async (targetUser, nextValue) => {
    try {
      const res = await api.put(`/auth/users/${targetUser._id}/auto-disburse`, { autoDisburse: nextValue })
      const updated = res?.data?.user
      if (updated?._id) {
        setUsers(prev => prev.map(u => (String(u._id) === String(updated._id) ? { ...u, autoDisburse: updated.autoDisburse } : u)))
        // If the user is in displayedUsers slice, update that too via users effect, but ensure immediate UI feedback
        setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(updated._id) ? { ...u, autoDisburse: updated.autoDisburse } : u)))
      }
    } catch (e) {
      console.error('Failed to update manual disburse', e)
      alert(e?.response?.data?.error || 'Failed to update manual disburse')
    }
  }

  const updateUserInLists = (updatedUser) => {
    if (!updatedUser?._id) return
    setUsers(prev => prev.map(u => (String(u._id) === String(updatedUser._id) ? { ...u, ...updatedUser } : u)))
    setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(updatedUser._id) ? { ...u, ...updatedUser } : u)))
  }

  const handleAdminUserProfileSaved = (updatedUser, successMessage) => {
    updateUserInLists(updatedUser)
    setEditingUser(null)
    alert(successMessage || 'User profile updated successfully')
  }

  const getRoleInfo = (role) => {
    const roles = {
      M: { name: "Management", color: "#8b5cf6", bgColor: "#f3f4f6" },
      L: { name: "Landlord", color: "#3b82f6", bgColor: "#eff6ff" },
      T: { name: "Tenant", color: "#10b981", bgColor: "#ecfdf5" },
      A: { name: "Admin", color: "#ef4444", bgColor: "#fef2f2" },
      Mbr: { name: "Member", color: "#f59e0b", bgColor: "#fffbeb" },
    }
    return roles[role] || { name: "Unknown", color: "#6b7280", bgColor: "#f9fafb" }
  }

  // Collect distinct roles: global role + property-specific roles
  const getDistinctRoles = (u) => {
    const codes = new Set()
    if (u?.role) codes.add(u.role)
    if (Array.isArray(u?.propertyRoles)) {
      u.propertyRoles.forEach(pr => {
        if (pr?.role) codes.add(pr.role)
      })
    }
    return Array.from(codes)
  }

  // Helper to determine if auto-disbursement should be enabled by default
  const shouldAutoDisburseByDefault = (user) => {
    const hasBankDetails = user.bankName && user.bankName.trim() && user.bankAccNumber && user.bankAccNumber.trim()
    if (!hasBankDetails) return false

    // Check direct role
    if (user.role === 'M' || user.role === 'L') return true
    if (user.role === 'T') return false

    // Check property roles
    const distinctRoles = getDistinctRoles(user)
    if (distinctRoles.includes('M') || distinctRoles.includes('L')) return true

    return false
  }

  const getInitials = (name) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  // Admin: fetch a user's wallet balance and transactions
  const fetchUserWalletBalanceAndTransactions = async (userId) => {
    try {
      setUserWalletTransactionsLoading(true)
      const [balRes, txRes] = await Promise.all([
        api.get(`/wallet/user-wallet/${userId}`),
        api.get(`/wallet/transactions/${userId}`)
      ])
      const bal = Number(balRes?.data?.wallet?.balance || 0)
      setUserWalletId(balRes?.data?.wallet?._id || null)
      setUserWalletBalance(bal)
      const txs = Array.isArray(txRes?.data?.transactions) ? txRes.data.transactions : []
      setUserWalletTransactions(txs)
    } catch (e) {
      console.error('Failed to fetch user wallet data', e)
      alert('Failed to load wallet for user')
    } finally {
      setUserWalletTransactionsLoading(false)
    }
  }

  const fetchFilteredUserWalletTransactions = async () => {
    if (!selectedUser?._id) return
    try {
      setUserWalletTransactionsLoading(true)
      let url = `/wallet/transactions/${selectedUser._id}`
      const params = new URLSearchParams()
      if (userWalletStartDate) params.append('startDate', userWalletStartDate)
      if (userWalletEndDate) params.append('endDate', userWalletEndDate)
      if (params.toString()) url += `?${params.toString()}`
      const res = await api.get(url)
      setUserWalletTransactions(Array.isArray(res?.data?.transactions) ? res.data.transactions : [])
    } catch (e) {
      console.error('Failed to fetch filtered transactions', e)
      alert('Failed to filter transactions')
    } finally {
      setUserWalletTransactionsLoading(false)
    }
  }

  // Admin: impersonate a company's manager and navigate to FRP showing that company
  const handleCompanyView = async (company) => {
    const managerId = typeof company?.user_id === 'object' ? company.user_id?._id : company?.user_id
    await startImpersonationForUser(managerId, {
      destination: `/app/frp?companyId=${company._id}`,
      missingTargetMessage: 'No manager found for this company',
      failureMessage: 'Failed to impersonate manager',
      noTokenMessage: 'Failed to impersonate manager: No token returned',
    })
  }

  // Admin: impersonate a property's manager and navigate to FRP showing that property
  const handlePropertyView = async (property) => {
    const companyId = (property.managementCompany && property.managementCompany._id) || property.managementCompany || null
    const company = companyId ? companies.find(c => String(c._id) === String(companyId)) : null
    const managerId =
      (property.manager && property.manager._id)
        ? property.manager._id
        : (typeof property.manager === 'string' && property.manager)
          ? property.manager
          : (company && typeof company.user_id === 'object' && company.user_id?._id)
            ? company.user_id._id
            : (company && company.user_id) || null

    const companyQuery = companyId ? `companyId=${companyId}` : ''
    const propQuery = property?._id ? `propertyId=${property._id}` : ''
    const qs = [companyQuery, propQuery].filter(Boolean).join('&')

    await startImpersonationForUser(managerId, {
      destination: qs ? `/app/frp?${qs}` : '/app/frp',
      missingTargetMessage: 'No manager found for this property',
      failureMessage: 'Failed to impersonate manager',
      noTokenMessage: 'Failed to impersonate manager: No token returned',
    })
  }

  // Admin actions for management companies (lock / remove)
  const handleCompanyAdminAction = async (company, action) => {
    if (!company?._id) return
    try {
      if (action === 'remove') {
        if (!window.confirm(`Remove management company "${company.companyName}"?\n\nThis action cannot be undone.`)) {
          return
        }
        await api.delete(`/management-company/admin/${company._id}`)
        setCompanies(prev => prev.filter(c => String(c._id) !== String(company._id)))
        setExpandedCompanyIds(prev => {
          const next = new Set(prev)
          next.delete(company._id)
          return next
        })
        alert('Management company removed')
        return
      }

      if (action === 'lock') {
        const nextLocked = !company.locked
        const res = await api.put(`/management-company/admin/${company._id}/lock`, { locked: nextLocked })
        const updated = res?.data?.managementCompany || { ...company, locked: nextLocked }
        setCompanies(prev =>
          prev.map(c => (String(c._id) === String(company._id) ? { ...c, locked: updated.locked } : c))
        )
        alert(updated.locked ? 'Company blocked' : 'Company unblocked')
        return
      }
    } catch (e) {
      console.error('Company admin action error', e)
      alert(e?.response?.data?.error || 'Failed to perform action on company')
    } finally {
      setCompanyActionsDropdownOpenId(null)
    }
  }

  // Admin actions for properties (lock / remove)
  const handlePropertyAdminAction = async (property, action) => {
    if (!property?._id) return
    try {
      if (action === 'remove') {
        if (!window.confirm(`Remove property "${property.name}" and all its units?\n\nThis action cannot be undone.`)) {
          return
        }
        await api.delete(`/frp/admin/properties/${property._id}`)
        setProperties(prev => prev.filter(p => String(p._id) !== String(property._id)))
        setExpandedPropertyIds(prev => {
          const next = new Set(prev)
          next.delete(property._id)
          return next
        })
        setUnitsByPropertyId(prev => {
          const next = new Map(prev)
          next.delete(property._id)
          return next
        })
        setUnitsLoadingByPropertyId(prev => {
          const next = new Map(prev)
          next.delete(property._id)
          return next
        })
        setUnitsShowCountByPropertyId(prev => {
          const next = new Map(prev)
          next.delete(property._id)
          return next
        })
        alert('Property removed')
        return
      }

      if (action === 'lock') {
        const nextLocked = !property.locked
        const res = await api.put(`/frp/admin/properties/${property._id}/lock`, { locked: nextLocked })
        const updated = res?.data?.property || { ...property, locked: nextLocked }
        setProperties(prev =>
          prev.map(p => (String(p._id) === String(property._id) ? { ...p, locked: updated.locked } : p))
        )
        alert(updated.locked ? 'Property blocked' : 'Property unblocked')
        return
      }
    } catch (e) {
      console.error('Property admin action error', e)
      alert(e?.response?.data?.error || 'Failed to perform action on property')
    } finally {
      setPropertyActionsDropdownOpenId(null)
    }
  }

  const clearUserWalletDateFilters = () => {
    setUserWalletStartDate('')
    setUserWalletEndDate('')
    if (selectedUser?._id) fetchUserWalletBalanceAndTransactions(selectedUser._id)
  }

  const openUserWalletDeleteConfirm = (tx) => {
    if (tx?.type === 'opening_balance') {
      setUserWalletDeleteError('Opening balance transactions cannot be deleted from wallet history.')
      return
    }
    setUserWalletTxToDelete(tx || null)
    setUserWalletDeleteError('')
    setShowUserWalletDeleteConfirm(true)
  }

  const handleEditUserWalletOpeningBalance = async (transaction) => {
    if (!canEditSplits || !selectedUser?._id || !transaction?.synthetic) return

    const currentValue = Number(transaction?.runningBalance || transaction?.amount || 0)
    const nextValueRaw = window.prompt('Enter the new opening balance brought forward:', String(currentValue))
    if (nextValueRaw === null) return

    const nextValue = Number(String(nextValueRaw).replace(/,/g, '').trim())
    if (!Number.isFinite(nextValue)) {
      alert('Invalid opening balance')
      return
    }

    try {
      setUserWalletTransactionsLoading(true)
      await api.put(`/wallet/admin/opening-balance/${selectedUser._id}`, {
        openingBalance: nextValue
      })

      const balRes = await api.get(`/wallet/user-wallet/${selectedUser._id}`)
      const bal = Number(balRes?.data?.wallet?.balance || 0)
      setUserWalletBalance(bal)
      setUserWalletId(balRes?.data?.wallet?._id || null)
      setUserWalletBalancesById(prev => ({ ...prev, [selectedUser._id]: bal }))

      await fetchFilteredUserWalletTransactions()
      fetchWalletBalances()
    } catch (e) {
      console.error('Failed to update opening balance', e)
      alert(e?.response?.data?.error || 'Failed to update opening balance')
    } finally {
      setUserWalletTransactionsLoading(false)
    }
  }

  const closeUserWalletDeleteConfirm = () => {
    if (userWalletDeleting) return
    setShowUserWalletDeleteConfirm(false)
    setUserWalletTxToDelete(null)
    setUserWalletDeleteError('')
  }

  const confirmDeleteUserWalletTransaction = async () => {
    if (!userWalletTxToDelete?._id) return
    if (!selectedUser?._id) return

    try {
      setUserWalletDeleting(true)
      setUserWalletDeleteError('')
      const config = userWalletId ? { params: { walletId: userWalletId } } : undefined
      await api.delete(`/frp/admin/wallet-transactions/${userWalletTxToDelete._id}`, config)
      setShowUserWalletDeleteConfirm(false)
      setUserWalletTxToDelete(null)

      const balRes = await api.get(`/wallet/user-wallet/${selectedUser._id}`)
      const bal = Number(balRes?.data?.wallet?.balance || 0)
      setUserWalletBalance(bal)
      setUserWalletId(balRes?.data?.wallet?._id || userWalletId || null)
      setUserWalletBalancesById(prev => ({ ...prev, [selectedUser._id]: bal }))

      await fetchFilteredUserWalletTransactions()
      fetchWalletBalances()
    } catch (e) {
      console.error('Failed to delete user wallet transaction', e)
      setUserWalletDeleteError(e?.response?.data?.error || e?.message || 'Failed to delete transaction')
    } finally {
      setUserWalletDeleting(false)
    }
  }

  // Withdrawal charges functions
  const fetchWithdrawalCharges = async () => {
    try {
      setWithdrawalChargesLoading(true)
      const res = await api.get('/frp/withdrawal-charges')
      setWithdrawalCharges(res?.data?.charges || [])
    } catch (e) {
      console.error('Failed to fetch withdrawal charges', e)
      alert('Failed to load withdrawal charges')
    } finally {
      setWithdrawalChargesLoading(false)
    }
  }

  const saveWithdrawalCharges = async () => {
    try {
      setWithdrawalChargesSaving(true)
      const res = await api.put('/frp/withdrawal-charges', { charges: withdrawalCharges })
      alert(res?.data?.message || 'Withdrawal charges updated successfully')
      setShowWithdrawalChargesModal(false)
    } catch (e) {
      console.error('Failed to save withdrawal charges', e)
      alert(e?.response?.data?.error || 'Failed to save withdrawal charges')
    } finally {
      setWithdrawalChargesSaving(false)
    }
  }

  const addWithdrawalChargeRow = () => {
    const lastCharge = withdrawalCharges[withdrawalCharges.length - 1]
    const newMin = lastCharge ? lastCharge.max + 1 : 1
    const newMax = newMin + 99
    setWithdrawalCharges(prev => [...prev, { id: Date.now(), min: newMin, max: newMax, charge: 0 }])
  }

  const removeWithdrawalChargeRow = (id) => {
    const chargeToDelete = withdrawalCharges.find(c => c.id === id)
    const confirmMessage = chargeToDelete
      ? `Are you sure you want to delete this withdrawal charge row?\n\nRange: ${chargeToDelete.min} - ${chargeToDelete.max}\nCharge: ${chargeToDelete.charge}\n\nThis action cannot be undone.`
      : 'Are you sure you want to delete this withdrawal charge row?\n\nThis action cannot be undone.'

    if (window.confirm(confirmMessage)) {
      setWithdrawalCharges(prev => prev.filter(c => c.id !== id))
    }
  }

  const updateWithdrawalCharge = (id, field, value) => {
    const numValue = Number(value)
    if (!Number.isFinite(numValue) || numValue < 0) return
    setWithdrawalCharges(prev => prev.map(c => c.id === id ? { ...c, [field]: numValue } : c))
  }

  // Compute disbursement fee using current withdrawal charge brackets
  // eslint-disable-next-line no-unused-vars
  const computeDisbursementFee = (amount) => {
    const amt = Number(amount || 0)
    if (!Number.isFinite(amt) || amt <= 0) return 0
    const brackets = Array.isArray(withdrawalCharges) ? withdrawalCharges : []
    const match = brackets.find(b => amt >= Number(b?.min || 0) && amt <= Number(b?.max ?? Infinity))
    return Number(match?.charge || 0)
  }

  const formatRlTransactionDescription = (t) => {
    const base = t?.description || ''
    if (!(t?.type === 'disbursement' || t?.type === 'disbursement_failed')) return base
    if (t?.user && t.user.name) return `${base} — to ${t.user.name}`
    const bankName = t?.bankName || ''
    const bankAcc = t?.bankAccNumber || ''
    const paybill = t?.paybill || ''
    const parts = []
    if (bankName) parts.push(bankName)
    if (bankAcc) {
      const accStr = String(bankAcc)
      const last4 = accStr.slice(-4)
      parts.push(`Acc ${last4}`)
    }
    if (paybill) parts.push(`Paybill ${paybill}`)
    const recipient = parts.join(' • ')
    return recipient ? `${base} — to ${recipient}` : base
  }

  const getRlDisbursementOutAmount = (t) => {
    const amt = Number(t?.amount || 0)
    if (t?.type === 'disbursement') return Math.abs(amt)
    if (t?.type !== 'disbursement_failed') return null
    if (amt) return Math.abs(amt)
    const desc = String(t?.description || '')
    const m = desc.match(/Failed processing disbursement amount\\s+(-?\\d+(?:\\.\\d+)?)/i)
    if (!m) return 0
    const parsed = Number(m[1])
    return Number.isFinite(parsed) ? Math.abs(parsed) : 0
  }

  // Prepare FRP wallet transactions with computed balances and optional sorting
  const rlTransactionsPrepared = (() => {
    let base = Array.isArray(rlWalletTransactions) ? rlWalletTransactions : []

    // Filter by AC and TransID if provided
    if (rlWalletAcSearch.trim()) {
      const q = rlWalletAcSearch.toLowerCase()
      base = base.filter(t => (t.description || '').toLowerCase().includes(q))
    }
    if (rlWalletTransIdSearch.trim()) {
      const q = rlWalletTransIdSearch.toLowerCase()
      base = base.filter(t => (t.description || '').toLowerCase().includes(q))
    }

    // Sort newest first strictly by timestamp; if equal time, show negatives before positives
    const sorted = [...base].sort((a, b) => {
      const at = new Date(a.timestamp).getTime()
      const bt = new Date(b.timestamp).getTime()
      if (bt !== at) return bt - at
      const aNeg = (a.amount || 0) < 0
      const bNeg = (b.amount || 0) < 0
      if (aNeg !== bNeg) return aNeg ? -1 : 1
      return 0
    })

    // Compute running balance from newest to oldest using balance as of endDate
    let running = Number(walletModalBalance || 0)
    const withBalances = sorted.map((t) => {
      const hasBalanceSnapshot = t?.balanceBefore !== null &&
        t?.balanceBefore !== undefined &&
        t?.balanceAfter !== null &&
        t?.balanceAfter !== undefined &&
        Number.isFinite(Number(t.balanceBefore)) &&
        Number.isFinite(Number(t.balanceAfter))
      const row = { ...t, _computedBalance: hasBalanceSnapshot ? Number(t.balanceAfter) : running }
      running = hasBalanceSnapshot ? Number(t.balanceBefore) : running - Number(t?.amount || 0)
      return row
    })

    if (rlWalletSort === 'desc') {
      return [...withBalances].sort((a, b) => (b._computedBalance - a._computedBalance))
    }
    if (rlWalletSort === 'asc') {
      return [...withBalances].sort((a, b) => (a._computedBalance - b._computedBalance))
    }
    return withBalances
  })()

  // Period totals for RL transactions (Revenue = positive platform fees; Disbursed = disbursement entries)
  const rlPeriodTotals = (() => {
    const base = Array.isArray(rlWalletTransactions) ? rlWalletTransactions : []
    let revenue = 0
    let disbursed = 0
    for (const t of base) {
      const amt = Number(t?.amount || 0)
      if ((t?.type === 'platform_fee' || t?.type === 'payment') && amt > 0) revenue += amt
      if (t?.type === 'disbursement') disbursed += Math.abs(amt)
    }
    return { revenue, disbursed }
  })()

  const adminWalletTransactionsPrepared = (() => {
    const base = Array.isArray(adminWalletTransactions) ? adminWalletTransactions : []
    const sorted = [...base].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    let running = Number(adminWalletBalance || 0)
    return sorted.map((t) => {
      const hasBalanceSnapshot = t?.balanceBefore !== null &&
        t?.balanceBefore !== undefined &&
        t?.balanceAfter !== null &&
        t?.balanceAfter !== undefined &&
        Number.isFinite(Number(t.balanceBefore)) &&
        Number.isFinite(Number(t.balanceAfter))
      const row = { ...t, _computedBalance: hasBalanceSnapshot ? Number(t.balanceAfter) : running }
      running = hasBalanceSnapshot ? Number(t.balanceBefore) : running - Number(t?.amount || 0)
      return row
    })
  })()
  //toggle wallet disburse on
  const [autoDisburse, setAutoDisburse] = useState(false);

	  // Fetch default state on mount
	  useEffect(() => {
		const fetchAutoDisburse = async () => {
		  try {
			const res = await fetch("/api/wallet/auto-disburse"); // simple GET
			const data = await res.json();
			setAutoDisburse(data.autoDisburse);
		  } catch (err) {
			console.error("Error fetching auto-disburse state:", err);
		  }
		};

		fetchAutoDisburse();
	  }, []);

	  const toggleAllWallets = async () => {
		try {
		  const res = await fetch("/api/wallet/toggle-all-disburse"); // simple GET toggle
		  const data = await res.json();
		  setAutoDisburse(data.autoDisburse);
		  alert(`Auto-disburse has been ${data.autoDisburse ? "enabled" : "disabled"} for all wallets`);
		} catch (err) {
		  console.error("Error toggling wallets:", err);
		  alert("Failed to update wallets");
		}
	  };
	//Beginning of balance enquiry trigger
	const [mpesaResult, setMpesaResult] = useState(null);
  const [mpesaBalanceStatus, setMpesaBalanceStatus] = useState({ loading: false, source: null, error: null })

	useEffect(() => {
	  // 1️⃣ Send request to Safaricom to trigger new balance fetch
	  const requestBalance = async () => {
		try {
		  setMpesaBalanceStatus({ loading: true, source: null, error: null });
		  await api.post('/frp/admin/mpesa/balance/refresh');
		} catch (err) {
		  console.error("Error sending balance request:", err);
      setMpesaBalanceStatus(prev => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.error || err?.message || 'Failed to refresh M-Pesa balance'
      }));
		}
	  };

	  requestBalance(); // call once on page load

	  // 2️⃣ Poll backend for latest saved balances
	  const fetchBalances = async () => {
		try {
		  const res = await api.get('/frp/admin/mpesa/balance/latest');
		  const data = res?.data || {};
		  setMpesaResult(data?.data || null);
      setMpesaBalanceStatus({
        loading: false,
        source: data?.source || null,
        error: data?.success ? null : 'Latest M-Pesa balance is unavailable'
      });
		} catch (err) {
		  console.error("Error fetching latest balances:", err);
      setMpesaBalanceStatus({
        loading: false,
        source: null,
        error: err?.response?.data?.error || err?.message || 'Failed to fetch latest M-Pesa balance'
      });
		}
	  };

	  fetchBalances(); // immediate fetch
	  const interval = setInterval(fetchBalances, 4000); // refresh every 4s

	  return () => clearInterval(interval); // cleanup on unmount
	}, []);

  return (
    <div className="pb-6">
          {/* Local UI State for Edit Percentage modal */}
          {/* Placed here to avoid top-of-file state changes; hooks assumed already declared above if needed */}
          {/* Welcome Section */}
          <div className="mb-4 sm:mb-8">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white shadow-2xl border border-emerald-600">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center text-white text-lg sm:text-xl font-bold border-2 border-white/20 flex-shrink-0">
                    {getInitials(user?.name)}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold text-white">
                      <span className="hidden sm:inline"> - </span>
                      <span className="block sm:inline">Welcome {user?.name ? user.name.split(' ')[0] : 'Admin'}!</span>
                    </h2>
                    <div className="inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium mt-1 sm:mt-2 bg-white/10 text-white border border-white/20">
                      Administrator
                    </div>
                  </div>
                </div>

                {user?.role === 'A' && (
                  <div className="w-full lg:w-auto">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={toggleTenantPayRentVisibility}
                        className="w-full bg-white/10 text-white/80 hover:text-white px-2 py-1.5 rounded-md border border-white/20 hover:border-white/40 transition-all duration-200 text-[10px] sm:text-xs"
                      >
                        Tenant Pay Rent: {showTenantPayRent ? "On" : "Off"}
                      </button>

                      <button
                        onClick={toggleAllWallets}
                        className="w-full bg-white/10 text-white/80 hover:text-white px-2 py-1.5 rounded-md border border-white/20 hover:border-white/40 transition-all duration-200 text-[10px] sm:text-xs"
                      >
                        {autoDisburse ? "Disable Auto-Disburse" : "Enable Auto-Disburse"}
                      </button>

                      <div className="w-full bg-white/10 text-white/80 px-2 py-1.5 rounded-md border border-white/20 text-[10px] sm:text-xs truncate">
                        Wkng: {mpesaResult?.workingAccountBalance != null ? `Ksh ${Number(mpesaResult.workingAccountBalance).toLocaleString()}` : 'N/A'}
                      </div>

                      <div className="w-full bg-white/10 text-white/80 px-2 py-1.5 rounded-md border border-white/20 text-[10px] sm:text-xs truncate">
                        Ut.: {mpesaResult?.utilityAccountBalance != null ? `Ksh ${Number(mpesaResult.utilityAccountBalance).toLocaleString()}` : 'N/A'}
                      </div>

                      {mpesaBalanceStatus?.source && (
                        <div className="col-span-2 w-full px-2 py-2 rounded-md border border-white/20 bg-white/10 text-[10px] sm:text-xs text-white/80 truncate">
                          Source: {mpesaBalanceStatus.source === 'frp_fallback' ? 'FRP fallback' : mpesaBalanceStatus.source}
                        </div>
                      )}

                      {mpesaBalanceStatus?.error && (
                        <div className="col-span-2 w-full px-2 py-2 rounded-md border border-red-200/30 bg-red-500/10 text-[10px] sm:text-xs text-red-100 truncate">
                          {mpesaBalanceStatus.error}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Wallet Stats Section */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
            {/* FRP Wallet */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200 col-span-1">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div>
                  <p className="text-sm sm:text-sm font-medium text-gray-600 mb-1">{walletLabels.platform}</p>
                  <p className="mt-1 mb-2 text-xs text-gray-500">(Platform Fee: {platformSplit.platformFeePercentage}%) </p>
                  <p className="text-sm sm:text-sm font-bold text-gray-900">{formatMoney(walletBalances.platform)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Icon name="wallet" size="1.25rem" className="text-gray-700 sm:w-6 sm:h-6" />
                </div>
              </div>
              <div className="mb-2">
                <label className="inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!systemAutoDisburse.platform}
                    onChange={(e) => toggleSystemWalletAutoDisburse('platform', e.target.checked)}
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-emerald-600 rounded-full relative transition-colors">
                    <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-all peer-checked:left-5" />
                  </div>
                  <span className="ml-2 text-xs text-gray-700">Manual disburse</span>
                </label>
              </div>
              <button
                onClick={() => {
                  setWalletKey('platform')
                  setWalletModalTitle('FRP Wallet Transactions')
                  const { start, end } = getCurrentMonthRange()
                  setRLWalletStartDate(start)
                  setRLWalletEndDate(end)
                  fetchSystemWalletTransactions('platform', start, end)
                  // Ensure fee brackets are loaded for Fee column
                  if (!Array.isArray(withdrawalCharges) || withdrawalCharges.length === 0) fetchWithdrawalCharges()
                  setShowRLWalletTransactionsModal(true)
                }}
                className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
              >
                View Transactions
              </button>
              <button
                onClick={() => openEditPercentage('platform')}
                disabled={!canEditSplits}
                title={canEditSplits ? '' : 'Only admins can edit percentages'}
                className="mt-2 w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors text-xs sm:text-sm font-medium"
              >
                Settings
              </button>
            </div>

            {/* All Wallets Balances */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div>
                  <p className="text-sm sm:text-sm font-medium text-gray-600 mb-1">All Wallets Balances</p>
                  <p className="text-sm sm:text-sm font-bold text-gray-900">{formatMoney(allUsersWalletsSum)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={openAllWalletsBalancesModal}
                  className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
                >
                  View
                </button>
                <button
                  onClick={openAdminWalletModal}
                  className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
                >
                  Admin Wallet
                </button>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleDisburse}
                    disabled={standardDisburseSubmitting}
                    className="w-full sm:flex-1 bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70 transition-colors text-xs sm:text-sm font-medium"
                  >
                    {standardDisburseSubmitting ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Disbursing...
                      </span>
                    ) : 'Disburse'}
                  </button>
                  <button
                    onClick={openFrpDisburseModal}
                    className="w-full sm:flex-1 bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
                    title="Choose users to disburse via FRP"
                  >
                    Disburse via FRP
                  </button>
                  <button
                    onClick={() => {
                      fetchWithdrawalCharges()
                      setShowWithdrawalChargesModal(true)
                    }}
                    className="w-full sm:w-auto bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center"
                    title="Edit Withdrawal Charges"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
                {standardDisburseSubmitting && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-900">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-emerald-100">
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                        </div>
                        <div>
                          <p className="font-semibold text-emerald-900">Standard disbursement is running</p>
                          <p className="mt-1 text-emerald-800">
                            The app is preparing the payout batch, sending it to the server, and refreshing wallet totals after completion.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:w-auto">
                        <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Status</div>
                          <div className="mt-1 font-semibold">In progress</div>
                        </div>
                        <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Elapsed</div>
                          <div className="mt-1 font-semibold">{standardDisburseElapsedSeconds}s</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 1: validate pending payouts</span>
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 2: submit disbursement</span>
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 3: refresh balances and logs</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Marketing Wallet */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-white/20">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div>
                  <p className="text-sm sm:text-sm font-medium text-gray-600 mb-1">{walletLabels.marketing}</p>
                  <p className="text-sm sm:text-sm font-bold text-gray-800">{formatMoney(walletBalances.marketing)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Icon name="wallet" size="1.25rem" className="text-gray-700 sm:w-6 sm:h-6" />
                </div>
              </div>
              <div className="mb-2">
                <label className="inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!systemAutoDisburse.marketing}
                    onChange={(e) => toggleSystemWalletAutoDisburse('marketing', e.target.checked)}
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-emerald-600 rounded-full relative transition-colors">
                    <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-all peer-checked:left-5" />
                  </div>
                  <span className="ml-2 text-xs text-gray-700">Manual disburse</span>
                </label>
              </div>
              <button
                onClick={() => {
                  setWalletKey('marketing')
                  setWalletModalTitle('Marketing Wallet Transactions')
                  const { start, end } = getCurrentMonthRange()
                  setRLWalletStartDate(start)
                  setRLWalletEndDate(end)
                  fetchSystemWalletTransactions('marketing', start, end)
                  if (!Array.isArray(withdrawalCharges) || withdrawalCharges.length === 0) fetchWithdrawalCharges()
                  setShowRLWalletTransactionsModal(true)
                }}
                className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
              >
                View Transactions
              </button>
              <button
                onClick={() => openEditPercentage('marketing')}
                disabled={!canEditSplits}
                title={canEditSplits ? '' : 'Only admins can edit percentages'}
                className="mt-2 w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors text-xs sm:text-sm font-medium"
              >
                Settings
              </button>
            </div>

            {/* Engineering Wallet */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-white/20">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div>
                  <p className="text-sm sm:text-sm font-medium text-gray-600 mb-1">{walletLabels.engineering}</p>
                  <p className="text-sm sm:text-sm font-bold text-gray-800">{formatMoney(walletBalances.engineering)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Icon name="wallet" size="1.25rem" className="text-gray-600 sm:w-6 sm:h-6" />
                </div>
              </div>
              <div className="mb-2">
                <label className="inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!systemAutoDisburse.engineering}
                    onChange={(e) => toggleSystemWalletAutoDisburse('engineering', e.target.checked)}
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-emerald-600 rounded-full relative transition-colors">
                    <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-all peer-checked:left-5" />
                  </div>
                  <span className="ml-2 text-xs text-gray-700">Manual disburse</span>
                </label>
              </div>
              <button
                onClick={() => {
                  setWalletKey('engineering')
                  setWalletModalTitle('Engineering Wallet Transactions')
                  const { start, end } = getCurrentMonthRange()
                  setRLWalletStartDate(start)
                  setRLWalletEndDate(end)
                  fetchSystemWalletTransactions('engineering', start, end)
                  if (!Array.isArray(withdrawalCharges) || withdrawalCharges.length === 0) fetchWithdrawalCharges()
                  setShowRLWalletTransactionsModal(true)
                }}
                className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
              >
                View Transactions
              </button>
              <button
                onClick={() => openEditPercentage('engineering')}
                disabled={!canEditSplits}
                title={canEditSplits ? '' : 'Only admins can edit percentages'}
                className="mt-2 w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors text-xs sm:text-sm font-medium"
              >
                Settings
              </button>
            </div>
          </div>

          {/* Edit Percentage Modal */}
          {showEditPercentageModal && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
              <div className="w-full max-w-sm bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                    {editTarget === 'engineering' ? 'Engineering Wallet Settings' : editTarget === 'marketing' ? 'Marketing Wallet Settings' : 'FRP Wallet Settings'}
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  {(editTarget === 'engineering' || editTarget === 'marketing') && (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                      <input
                        type="number"
                        value={editPercentageValue}
                        onChange={(e) => setEditPercentageValue(e.target.value)}
                        placeholder="e.g., 10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </>
                  )}
                  {loadingSplit
                    ? (
                      <p className="mt-2 text-xs text-gray-500">Loading current settings...</p>
                    )
                    : (
                      (editTarget === 'engineering' || editTarget === 'marketing')
                        ? (
                          <p className="mt-2 text-xs text-gray-500">
                            Current: Engineering {platformSplit.engineeringWalletPercentage}% • Marketing {platformSplit.marketingWalletPercentage}%
                          </p>
                        )
                        : editTarget === 'platform'
                          ? (
                            <p className="mt-2 text-xs text-gray-500">
                              Current RL Fee: {platformSplit.platformFeePercentage}%
                            </p>
                          )
                          : null
                    )}
                  {/* Bank details fields */}
                  {editTarget && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                        <BankCombobox
                          value={editTarget === 'engineering' ? bankDetails.engineeringBankName : editTarget === 'marketing' ? bankDetails.marketingBankName : bankDetails.platformBankName}
                          banksCatalog={banksCatalog}
                          onChange={(name) => setBankDetails(prev => editTarget === 'engineering'
                            ? { ...prev, engineeringBankName: name }
                            : editTarget === 'marketing'
                              ? { ...prev, marketingBankName: name }
                              : { ...prev, platformBankName: name }
                          )}
                          onSelectBank={(bank) => {
                            if (!bank) return
                            setBankDetails(prev => editTarget === 'engineering'
                              ? { ...prev, engineeringBankName: bank.name || '' }
                              : editTarget === 'marketing'
                                ? { ...prev, marketingBankName: bank.name || '' }
                                : { ...prev, platformBankName: bank.name || '' }
                            )
                            setBankDetailsPaybill(prev => editTarget === 'engineering'
                              ? { ...prev, engineeringPaybill: bank.paybill || '' }
                              : editTarget === 'marketing'
                                ? { ...prev, marketingPaybill: bank.paybill || '' }
                                : { ...prev, platformPaybill: bank.paybill || '' }
                            )
                          }}
                          placeholder="e.g., Bank of Example"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Paybill</label>
                        <input
                          type="text"
                          value={editTarget === 'engineering' ? bankDetailsPaybill.engineeringPaybill : editTarget === 'marketing' ? bankDetailsPaybill.marketingPaybill : bankDetailsPaybill.platformPaybill}
                          onChange={(e) => setBankDetailsPaybill(prev => editTarget === 'engineering'
                            ? { ...prev, engineeringPaybill: e.target.value }
                            : editTarget === 'marketing'
                              ? { ...prev, marketingPaybill: e.target.value }
                              : { ...prev, platformPaybill: e.target.value }
                          )}
                          placeholder="e.g., 123456"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number</label>
                        <input
                          type="text"
                          value={editTarget === 'engineering' ? bankDetails.engineeringBankAccountNumber : editTarget === 'marketing' ? bankDetails.marketingBankAccountNumber : bankDetails.platformBankAccountNumber}
                          onChange={(e) => setBankDetails(prev => editTarget === 'engineering'
                            ? { ...prev, engineeringBankAccountNumber: e.target.value }
                            : editTarget === 'marketing'
                              ? { ...prev, marketingBankAccountNumber: e.target.value }
                              : { ...prev, platformBankAccountNumber: e.target.value }
                          )}
                          placeholder="e.g., 0123456789"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-3">
                  <button
                    onClick={() => { setShowEditPercentageModal(false); setEditTarget(null) }}
                    className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={savePlatformSplit}
                    disabled={savingSplit}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm font-medium"
                  >
                    {savingSplit ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* FRP Wallet Summary (real data) */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-white/20 mb-4 sm:mb-8">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2">
                <Icon name="wallet" size="1rem" className="text-gray-700 sm:w-5 sm:h-5" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">RL Revenue Summary</h3>
              </div>
            </div>
            {/* Month/Year Selector (inline) */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 p-2 -mt-2">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={selectedSummaryMonth}
                    onChange={(e) => setSelectedSummaryMonth(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {getMonthName(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={selectedSummaryYear}
                    onChange={(e) => setSelectedSummaryYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                  >
                    {Array.from({ length: 5 }, (_, i) => getNairobiYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={fetchRlSummaryMetrics}
                  disabled={monthlySummaryLoading}
                  className="bg-emerald-600 text-white px-3 py-1 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors text-xs"
                >
                  {monthlySummaryLoading ? 'Loading...' : 'Update'}
                </button>
              </div>
              <div className="text-[11px] sm:text-xs text-gray-600">Period: {getSelectedPeriodLabel()}</div>
            </div>
            {/* Summary metrics derived from platform wallet endpoint */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600">Gross Revenue</p>
                <p className="text-base font-bold text-emerald-600">{formatMoney(rlSummary.grossRevenue)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600">Disbursed</p>
                <p className="text-base font-bold text-emerald-600">{formatMoney(rlSummary.disbursed)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600">RL Revenue</p>
                <p className="text-base font-bold text-gray-600">{formatMoney(rlSummary.rlRevenue)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600">All Wallets Balance</p>
                <p className="text-base font-bold text-gray-700">{formatMoney(allUsersWalletsSum)}</p>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  setWalletKey('all')
                  setWalletModalTitle('All App Transactions')
                  const { start, end } = getSelectedMonthRange()
                  setRLWalletStartDate(start)
                  setRLWalletEndDate(end)
                  fetchAllAppTransactions(start, end)
                  setShowRLWalletTransactionsModal(true)
                }}
                className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-xs sm:text-sm font-medium"
              >
                Paybill Deposit & Disbursement
              </button>
            </div>
          </div>

          {/* Users Management Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-white/20 mb-4 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
              <button
                onClick={() => setUsersCollapsed(!usersCollapsed)}
                className="flex items-center gap-2 text-gray-800 text-base sm:text-lg font-semibold hover:text-gray-600 transition-colors"
              >
                <Icon name="people" size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                Users Management
                <Icon
                  name={usersCollapsed ? "chevron-right" : "chevron-down"}
                  size="1rem"
                  className="text-gray-600 sm:w-5 sm:h-5 transition-transform duration-200"
                />
              </button>
              {!usersCollapsed && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="hidden sm:block text-xs sm:text-sm text-gray-600">
                    Showing {displayedUsers.length} of {users.filter(u => {
                      const name = (u.name || '').toLowerCase()
                      const email = (u.email || '').toLowerCase()
                      const idNumber = (u.idNumber || '').toLowerCase()
                      const objectId = (u._id ? String(u._id) : '').toLowerCase()
                      const q = searchQuery.toLowerCase()
                      return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                    }).length} users
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 hidden sm:inline">Sort:</span>
                    <button
                      onClick={() => {
                        if (!activeUsersSort || activeUsersSort.type !== 'wallet') {
                          setActiveUsersSort({ type: 'wallet', direction: 'desc' })
                        } else if (activeUsersSort.direction === 'desc') {
                          setActiveUsersSort({ type: 'wallet', direction: 'asc' })
                        } else {
                          setActiveUsersSort(null)
                        }
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all shadow-sm ${activeUsersSort?.type === 'wallet'
                        ? 'bg-emerald-600 text-white border border-emerald-700'
                        : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                        }`}
                      title="Toggle wallet sort"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span>Wallet</span>
                      {activeUsersSort?.type === 'wallet' && (
                        <Icon
                          name={activeUsersSort.direction === 'desc' ? 'chevron-up' : 'chevron-down'}
                          size="0.75rem"
                          className="text-current"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        if (!activeUsersSort || activeUsersSort.type !== 'due') {
                          setActiveUsersSort({ type: 'due', direction: 'desc' })
                        } else if (activeUsersSort.direction === 'desc') {
                          setActiveUsersSort({ type: 'due', direction: 'asc' })
                        } else {
                          setActiveUsersSort(null)
                        }
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all shadow-sm ${activeUsersSort?.type === 'due'
                        ? 'bg-emerald-600 text-white border border-emerald-700'
                        : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                        }`}
                      title="Toggle balance due sort"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span>Bal Due</span>
                      {activeUsersSort?.type === 'due' && (
                        <Icon
                          name={activeUsersSort.direction === 'desc' ? 'chevron-up' : 'chevron-down'}
                          size="0.75rem"
                          className="text-current"
                        />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {!usersCollapsed && (
              <>
                {usersLoading && (
                  <div className="mb-3 text-xs sm:text-sm text-gray-500">Loading users...</div>
                )}
                {/* Search Bar */}
                <div className="mb-4 sm:mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="search" size="1rem" className="text-gray-400 sm:w-5 sm:h-5" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search users by name, ID, or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Users Table - Desktop */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Name</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">ID</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Email</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Role</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Actions</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Bank</th>
                        <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium text-gray-700">Manual disburse</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {displayedUsers.map((user) => {
                        const distinctRoles = getDistinctRoles(user)
                        const kraSummary = renderUserKraSummary(user)
                        return (
                          <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 ${user.locked ? 'bg-red-400' : 'bg-gradient-to-br from-gray-400 to-gray-500'} rounded-full flex items-center justify-center text-white text-xs font-semibold`}>
                                  {getInitials(user.name)}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm sm:text-base font-medium text-gray-900">{user.name}</span>
                                  {user.locked && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                                      Blocked
                                    </span>
                                  )}
                                </div>
                                {kraSummary && (
                                  <div className={`mt-1 text-[11px] ${kraSummary.toneClassName}`}>{kraSummary.text}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <span className="text-xs sm:text-sm text-gray-900 font-mono">{user.idNumber || String(user._id)}</span>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <span className="text-xs sm:text-sm text-gray-600">{user.email}</span>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="flex flex-wrap gap-1.5">
                                {distinctRoles.map((code) => {
                                  const info = getRoleInfo(code)
                                  return (
                                    <span
                                      key={code}
                                      className="inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium"
                                      style={{ backgroundColor: info.bgColor, color: info.color }}
                                    >
                                      {info.name}
                                    </span>
                                  )
                                })}
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="relative" data-dropdown-container>
                                <button
                                  onClick={() => {
                                    const opening = dropdownOpen !== user._id
                                    setDropdownOpen(opening ? user._id : null)
                                    if (opening) {
                                      prefetchUserWalletsAndDues([user._id], { fetchWallet: true, fetchDue: false })
                                    }
                                  }}
                                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  Actions
                                  <Icon name="chevron-down" size="0.875rem" className="sm:w-4 sm:h-4" />
                                </button>

                                {dropdownOpen === user._id && (
                                  <div className="absolute right-0 top-full mt-1 w-40 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                    <div className="p-1">
                                      <button
                                        onClick={() => handleActionClick(user._id, 'Wallet')}
                                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                      >
                                        <Icon name="wallet" size="0.875rem" className="sm:w-4 sm:h-4" />
                                        Wallet
                                      </button>
                                      <button
                                        onClick={() => handleActionClick(user._id, 'View Account')}
                                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                      >
                                        <Icon name="person" size="0.875rem" className="sm:w-4 sm:h-4" />
                                        View Account
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingUser(user)
                                          setDropdownOpen(null)
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                      >
                                        <Icon name="pencil-square" size="0.875rem" className="sm:w-4 sm:h-4" />
                                        Edit Profile
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const newPinRaw = window.prompt('Enter new 4-digit PIN for this user:')
                                          let newPin = (newPinRaw || '').trim()
                                          if (!newPin) return
                                          newPin = newPin.replace(/\D/g, '')
                                          if (newPin.length !== 4) { alert('PIN must be exactly 4 digits'); return }
                                          try {
                                            const res = await api.put(`/auth/users/${user._id}/reset-pin`, { newPin })
                                            alert(res?.data?.message || 'PIN reset successfully')
                                          } catch (e) {
                                            alert(e?.response?.data?.error || 'Failed to reset PIN')
                                          }
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                      >
                                        <Icon name="key" size="0.875rem" className="sm:w-4 sm:h-4" />
                                        Change Password
                                      </button>
                                      {user.locked ? (
                                        <button
                                          onClick={async () => {
                                            try {
                                              const res = await api.put(`/auth/users/${user._id}/unlock`)
                                              alert(res?.data?.message || 'User unblocked')
                                              setUsers(prev => prev.map(u => (String(u._id) === String(user._id) ? { ...u, locked: false } : u)))
                                              setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(user._id) ? { ...u, locked: false } : u)))
                                            } catch (e) {
                                              alert(e?.response?.data?.error || 'Failed to unblock user')
                                            }
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                        >
                                          <Icon name="unlock" size="0.875rem" className="sm:w-4 sm:h-4" />
                                          Unblock
                                        </button>
                                      ) : (
                                        <button
                                          onClick={async () => {
                                            if (!window.confirm('Block this user? They will be unable to log in.')) return
                                            try {
                                              const res = await api.put(`/auth/users/${user._id}/lock`)
                                              alert(res?.data?.message || 'User blocked')
                                              setUsers(prev => prev.map(u => (String(u._id) === String(user._id) ? { ...u, locked: true } : u)))
                                              setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(user._id) ? { ...u, locked: true } : u)))
                                            } catch (e) {
                                              alert(e?.response?.data?.error || 'Failed to block user')
                                            }
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                                        >
                                          <Icon name="lock" size="0.875rem" className="sm:w-4 sm:h-4" />
                                          Block
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 text-[11px] leading-tight">
                                <div className="text-emerald-700">
                                  Wallet Bal: {userWalletBalancesById[user._id] === undefined ? '—' : formatMoney(Number(userWalletBalancesById[user._id] || 0))}
                                </div>
                                <div className="text-red-700">Bal due: {formatMoney(Number(userBalanceDueById[user._id] ?? 0))}</div>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="text-xs sm:text-sm text-gray-700 leading-tight">
                                <div className="font-medium truncate max-w-[10rem] sm:max-w-[14rem]" title={user.bankName || '-'}>{user.bankName || '-'}</div>
                                <div className="text-gray-500 font-mono truncate max-w-[10rem] sm:max-w-[14rem]" title={user.bankAccNumber || '-'}>{user.bankAccNumber || '-'}</div>
                                {user.locked && (
                                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-600">
                                    <Icon name="lock" size="0.75rem" /> Blocked
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="space-y-1">
                                <label className="inline-flex items-center cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={!!user.autoDisburse}
                                    disabled={!user.bankName || !user.bankAccNumber}
                                    onChange={(e) => {
                                      if (!user.bankName || !user.bankAccNumber) {
                                        alert('Set bank name and bank account number before enabling manual disburse')
                                        return
                                      }
                                      toggleUserAutoDisburse(user, e.target.checked)
                                    }}
                                  />
                                  <div className={`w-10 h-5 ${(!user.bankName || !user.bankAccNumber) ? 'bg-gray-300' : 'bg-gray-200'} peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-600 relative transition-colors`}>
                                    <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-all peer-checked:left-5" />
                                  </div>
                                  <span className="ml-2 text-xs text-gray-700">{user.autoDisburse ? 'On' : 'Off'}</span>
                                </label>
                                {(!user.bankName || !user.bankAccNumber) ? (
                                  <div className="text-[11px] text-gray-500">(add bank details)</div>
                                ) : (
                                  <div className="text-[11px] text-gray-500">
                                    {shouldAutoDisburseByDefault(user) ? (
                                      <span className="text-gray-700">Default: ON for M/L roles</span>
                                    ) : (
                                      <span className="text-gray-600">Default: OFF for T role</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Users Cards - Mobile */}
                <div className="sm:hidden space-y-3 block">
                  {displayedUsers.map((user) => {
                    const distinctRoles = getDistinctRoles(user)
                    const kraSummary = renderUserKraSummary(user)
                    return (
                      <div key={user._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 ${user.locked ? 'bg-red-400' : 'bg-gradient-to-br from-gray-400 to-gray-500'} rounded-full flex items-center justify-center text-white text-sm font-semibold`}>
                              {getInitials(user.name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">{user.name}</h4>
                                {user.locked && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                                    Blocked
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 font-mono">{user.idNumber || String(user._id)}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {distinctRoles.map((code) => {
                              const info = getRoleInfo(code)
                              return (
                                <span
                                  key={code}
                                  className="inline-block px-2 py-1 rounded-full text-[11px] font-medium"
                                  style={{ backgroundColor: info.bgColor, color: info.color }}
                                >
                                  {info.name}
                                </span>
                              )
                            })}
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-xs text-gray-600 break-all">{user.email}</p>
                          {kraSummary && (
                            <p className={`mt-1 text-[11px] ${kraSummary.toneClassName}`}>{kraSummary.text}</p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleActionClick(user._id, 'Wallet')}
                            className="flex-1 bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1"
                          >
                            <Icon name="wallet" size="0.875rem" />
                            Wallet
                          </button>
                          <button
                            onClick={() => handleActionClick(user._id, 'View Account')}
                            className="flex-1 bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1"
                          >
                            <Icon name="person" size="0.875rem" />
                            View
                          </button>
                          <button
                            onClick={() => { setMobileActionsUser(user); prefetchUserWalletsAndDues([user._id], { fetchWallet: true, fetchDue: false }) }}
                            className="flex-1 bg-emerald-100 text-emerald-800 px-3 py-2 rounded-lg text-xs font-medium hover:bg-emerald-200 transition-colors flex items-center justify-center gap-1"
                          >
                            <Icon name="chevron-down" size="0.875rem" />
                            Actions
                          </button>
                        </div>
                        <div className="mt-2 text-[11px] leading-tight">
                          <div className="text-emerald-700">Wallet Bal: {userWalletBalancesById[user._id] === undefined ? '—' : formatMoney(Number(userWalletBalancesById[user._id] || 0))}</div>
                          <div className="text-red-700">Bal due: {formatMoney(Number(userBalanceDueById[user._id] ?? 0))}</div>
                        </div>

                        {/* Manual disburse toggle (mobile) */}
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-700">Manual disburse</span>
                            <label className="inline-flex items-center cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={!!user.autoDisburse}
                                // Allow turning OFF even if bank details are missing; block turning ON without bank details
                                disabled={(!user.bankName || !user.bankAccNumber) && !user.autoDisburse}
                                onChange={(e) => {
                                  if ((!user.bankName || !user.bankAccNumber) && e.target.checked) {
                                    alert('Set bank name and bank account number before enabling manual disburse')
                                    return
                                  }
                                  toggleUserAutoDisburse(user, e.target.checked)
                                }}
                              />
                              <div className={`w-10 h-5 ${(!user.bankName || !user.bankAccNumber) ? 'bg-gray-300' : 'bg-gray-200'} peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-600 relative transition-colors`}>
                                <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-all peer-checked:left-5" />
                              </div>
                              <span className="ml-2 text-xs text-gray-700">{user.autoDisburse ? 'On' : 'Off'}</span>
                            </label>
                          </div>
                          <div className="text-[11px] text-gray-600">
                            <div><span className="font-medium">Bank:</span> {user.bankName || '-'}</div>
                            <div className="font-mono"><span className="font-medium">Acc:</span> {user.bankAccNumber || '-'}</div>
                            {(!user.bankName || !user.bankAccNumber) ? (
                              <div className="text-gray-500 mt-1">(add bank details)</div>
                            ) : (
                              <div className="mt-1">
                                {shouldAutoDisburseByDefault(user) ? (
                                  <span className="text-gray-700">Default: ON for M/L roles</span>
                                ) : (
                                  <span className="text-gray-600">Default: OFF for T role</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Show More/Less Buttons */}
                <div className="mt-4 sm:mt-6 text-center space-y-2 sm:space-y-0 sm:space-x-3 flex flex-col sm:flex-row justify-center">
                  {users.filter(u => {
                    const name = (u.name || '').toLowerCase()
                    const email = (u.email || '').toLowerCase()
                    const idNumber = (u.idNumber || '').toLowerCase()
                    const objectId = (u._id ? String(u._id) : '').toLowerCase()
                    const q = searchQuery.toLowerCase()
                    return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                  }).length > displayedUsers.length && (
                      <button
                        onClick={handleShowMore}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm sm:text-base font-medium"
                      >
                        Show More (+5)
                      </button>
                    )}

                  {showMoreCount > 3 && (
                    <button
                      onClick={() => setShowMoreCount(3)}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm sm:text-base font-medium"
                    >
                      Show Less
                    </button>
                  )}
                </div>

                {displayedUsers.length === 0 && searchQuery && (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <p className="text-sm sm:text-base">No users found matching "{searchQuery}"</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modules Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-white/20">
            <h3 className="flex items-center gap-2 text-gray-800 text-base sm:text-lg font-semibold mb-2">
              <Icon name="grid" size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
              Admin Modules
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-5">Access and manage system modules</p>

            {/* ForRent Module */}
            <div className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setForRentCollapsed(!forRentCollapsed)}
                className="w-full flex items-center justify-between p-3 sm:p-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg flex items-center justify-center">
                    <Icon name="house" size="1rem" className="text-white sm:w-5 sm:h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900">Rentals</h4>
                    <p className="text-xs sm:text-sm text-gray-600">Property management system</p>
                  </div>
                </div>
                <Icon
                  name={forRentCollapsed ? "chevron-right" : "chevron-down"}
                  size="1rem"
                  className="text-gray-600 sm:w-5 sm:h-5 transition-transform duration-200"
                />
              </button>

              {!forRentCollapsed && (
                <div className="border-t border-gray-200 bg-gray-50 p-3 sm:p-4 space-y-3">
                  {/* RL Share Editor */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">RL Share (%)</label>
                        <input
                          type="number"
                          value={rlShareValue}
                          onChange={(e) => setRlShareValue(e.target.value)}
                          placeholder="e.g., 1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={!canEditSplits}
                        />
                        <p className="mt-1 text-[11px] text-gray-500">Current RL Fee: {platformSplit.platformFeePercentage}%</p>
                      </div>
                      <div className="sm:w-auto w-full">
                        <button
                          onClick={savePlatformShare}
                          disabled={!canEditSplits || savingSplit}
                          className="w-full bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60 text-xs sm:text-sm font-medium"
                        >
                          {savingSplit ? 'Saving…' : 'Update'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Companies Subsection */}
                  <div className="border border-gray-200 rounded-lg bg-white">
                    <button
                      onClick={() => setCompaniesCollapsed(!companiesCollapsed)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="building" size="0.875rem" className="text-gray-700 sm:w-4 sm:h-4" />
                        <span className="text-sm font-medium text-gray-900">Companies</span>
                        <span className="text-xs text-gray-500">({companies.length})</span>
                      </div>
                      <Icon
                        name={companiesCollapsed ? "chevron-right" : "chevron-down"}
                        size="0.875rem"
                        className="text-gray-600 sm:w-4 sm:h-4 transition-transform duration-200"
                      />
                    </button>

                    {!companiesCollapsed && (
                      <div className="border-t border-gray-200 p-3">
                        {/* Companies Search */}
                        <div className="mb-3">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Icon name="search" size="0.875rem" className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search companies..."
                              value={companiesSearch}
                              onChange={(e) => setCompaniesSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Companies List with nested Properties and Units */}
                        <div className="space-y-2">
                          {companiesLoading ? (
                            <div className="text-xs text-gray-500 p-2">Loading companies...</div>
                          ) : (
                            companies
                              .filter(c =>
                                (c.companyName || '').toLowerCase().includes(companiesSearch.toLowerCase()) ||
                                (c.headOfficeLocation || '').toLowerCase().includes(companiesSearch.toLowerCase()) ||
                                (c.telephoneNumber || '').toLowerCase().includes(companiesSearch.toLowerCase())
                              )
                              .slice(0, companiesShowCount)
                              .map((company) => (
                                <div key={company._id} className="p-2 border border-gray-200 rounded-md">
                                  {/* Company Header */}
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h5 className="text-sm font-medium text-gray-900">{company.companyName}</h5>
                                      {company.locked && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-red-700 border border-red-200 mt-1">
                                          Blocked
                                        </span>
                                      )}
                                      <p className="text-xs text-gray-600">{company.telephoneNumber}</p>
                                      <p className="text-xs text-gray-500">{company.headOfficeLocation}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleCompanyView(company)}
                                        className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={() => toggleCompanyExpanded(company._id)}
                                        className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200"
                                      >
                                        {expandedCompanyIds.has(company._id) ? <Icon name="chevron-up" size="0.875rem" className="text-emerald-600 sm:w-4 sm:h-4" /> : <Icon name="chevron-down" size="0.875rem" className="text-emerald-600 sm:w-4 sm:h-4" />}
                                      </button>
                                      <div className="relative" data-dropdown-container>
                                        <button
                                          onClick={() =>
                                            setCompanyActionsDropdownOpenId(prev =>
                                              prev === company._id ? null : company._id
                                            )
                                          }
                                          className="p-1 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800"
                                          title="Company actions"
                                        >
                                          <span className="sr-only">Company actions</span>
                                          <span className="text-lg leading-none">⋮</span>
                                        </button>
                                        {companyActionsDropdownOpenId === company._id && (
                                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                            <button
                                              onClick={() => handleCompanyAdminAction(company, 'lock')}
                                              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                            >
                                              {company.locked ? 'Unblock' : 'Block'}
                                            </button>
                                            <button
                                              onClick={() => handleCompanyAdminAction(company, 'remove')}
                                              className="w-full text-left px-3 py-2 text-xs text-red-700 hover:bg-red-50"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Properties under Company */}
                                  {expandedCompanyIds.has(company._id) && (
                                    <div className="mt-2 border-t border-gray-100 pt-2 space-y-2">
                                      {properties
                                        .filter(p => (p.managementCompany?._id || p.managementCompany) === company._id)
                                        .map((p) => (
                                          <div key={p._id} className="p-2 border border-gray-100 rounded-md bg-gray-50">
                                            <div className="flex justify-between items-start">
                                              <div>
                                                <h6 className="text-sm font-semibold text-gray-900">{p.name}</h6>
                                                {p.locked && (
                                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-red-700 border border-red-200 mt-1">
                                                    Blocked
                                                  </span>
                                                )}
                                                <p className="text-xs text-gray-600">{p.address}</p>
                                                {p.unitCounts && (
                                                  <p className="text-[11px] text-gray-500">Units: {p.unitCounts.total} • Occupied: {p.unitCounts.occupied} • Vacant: {p.unitCounts.vacant}</p>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-2">
                                                <button
                                                  onClick={() => handlePropertyView(p)}
                                                  className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                                                >
                                                  View
                                                </button>
                                                <button
                                                  onClick={() => togglePropertyExpanded(p._id)}
                                                  className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200"
                                                >
                                                  {expandedPropertyIds.has(p._id) ? 'Hide Units' : 'Show Units'}
                                                </button>
                                                <div className="relative" data-dropdown-container>
                                                  <button
                                                    onClick={() =>
                                                      setPropertyActionsDropdownOpenId(prev =>
                                                        prev === p._id ? null : p._id
                                                      )
                                                    }
                                                    className="p-1 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800"
                                                    title="Property actions"
                                                  >
                                                    <span className="sr-only">Property actions</span>
                                                    <span className="text-lg leading-none">⋮</span>
                                                  </button>
                                                  {propertyActionsDropdownOpenId === p._id && (
                                                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                                      <button
                                                        onClick={() => handlePropertyAdminAction(p, 'lock')}
                                                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                                      >
                                                        {p.locked ? 'Unblock' : 'Block'}
                                                      </button>
                                                      <button
                                                        onClick={() => handlePropertyAdminAction(p, 'remove')}
                                                        className="w-full text-left px-3 py-2 text-xs text-red-700 hover:bg-red-50"
                                                      >
                                                        Remove
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Units under Property */}
                                            {expandedPropertyIds.has(p._id) && (
                                              <div className="mt-2 border-t border-gray-200 pt-2">
                                                {unitsLoadingByPropertyId.get(p._id) ? (
                                                  <div className="text-xs text-gray-500 p-2">Loading units...</div>
                                                ) : (
                                                  (() => {
                                                    const allUnits = unitsByPropertyId.get(p._id) || []
                                                    const showCount = unitsShowCountByPropertyId.get(p._id) || 5
                                                    const filteredUnits = allUnits
                                                    return (
                                                      <div className="space-y-2">
                                                        {filteredUnits.slice(0, showCount).map((u) => (
                                                          <div key={u._id} className="p-2 border border-gray-200 rounded bg-white">
                                                            <div className="flex justify-between items-start">
                                                              <div>
                                                                <p className="text-sm font-medium text-gray-900">Unit {u.label}</p>
                                                                <p className="text-xs text-gray-600">Rent: {formatMoney(u.rentAmount)} • Status: {u.status || (u.tenant ? 'Occupied' : 'Vacant')}</p>
                                                                <p className="text-[11px] text-gray-500">Balance: {formatMoney(u.currentBalance || 0)}</p>
                                                              </div>
                                                              <button
                                                                onClick={() => {
                                                                  // Reuse unit view modal from earlier admin modal
                                                                  setSelectedProperty(p)
                                                                  // create a minimal shape to reuse property modal? Use separate unit modal
                                                                  setSelectedUser(null)
                                                                  // For now, leverage property modal info; more detailed unit modal exists above for admin user wallet, but here we can open property modal
                                                                  // Better: open property modal and rely on View Units in FRPModule? Keeping simple: show unit info inline
                                                                }}
                                                                className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                                                              >
                                                                View
                                                              </button>
                                                            </div>
                                                          </div>
                                                        ))}
                                                        <div className="text-center space-x-2">
                                                          {allUnits.length > showCount && (
                                                            <button
                                                              onClick={() => setUnitsShowCountByPropertyId(prev => new Map(prev.set(p._id, showCount + 5)))}
                                                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                                                            >
                                                              Show More (+5)
                                                            </button>
                                                          )}
                                                          {showCount > 5 && (
                                                            <button
                                                              onClick={() => setUnitsShowCountByPropertyId(prev => new Map(prev.set(p._id, 5)))}
                                                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                                                            >
                                                              Show Less
                                                            </button>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )
                                                  })()
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              ))
                          )}
                        </div>
                        {/* Companies Show More/Less */}
                        <div className="mt-3 text-center space-x-2">
                          {companies.filter(c =>
                            (c.companyName || '').toLowerCase().includes(companiesSearch.toLowerCase()) ||
                            (c.headOfficeLocation || '').toLowerCase().includes(companiesSearch.toLowerCase()) ||
                            (c.telephoneNumber || '').toLowerCase().includes(companiesSearch.toLowerCase())
                          ).length > companiesShowCount && (
                              <button
                                onClick={() => setCompaniesShowCount(prev => prev + 5)}
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                              >
                                Show More (+5)
                              </button>
                            )}
                          {companiesShowCount > 5 && (
                            <button
                              onClick={() => setCompaniesShowCount(5)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                            >
                              Show Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Properties Subsection */}
                  <div className="border border-gray-200 rounded-lg bg-white">
                    <button
                      onClick={() => setPropertiesCollapsed(!propertiesCollapsed)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="house" size="0.875rem" className="text-emerald-600 sm:w-4 sm:h-4" />
                        <span className="text-sm font-medium text-gray-900">Properties</span>
                        <span className="text-xs text-gray-500">({properties.length})</span>
                      </div>
                      <Icon
                        name={propertiesCollapsed ? "chevron-right" : "chevron-down"}
                        size="0.875rem"
                        className="text-gray-600 sm:w-4 sm:h-4 transition-transform duration-200"
                      />
                    </button>

                    {!propertiesCollapsed && (
                      <div className="border-t border-gray-200 p-3">
                        {/* Properties Search */}
                        <div className="mb-3">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Icon name="search" size="0.875rem" className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search properties..."
                              value={propertiesSearch}
                              onChange={(e) => setPropertiesSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Properties List */}
                        <div className="space-y-2">
                          {propertiesLoading ? (
                            <div className="text-xs text-gray-500 p-2">Loading properties...</div>
                          ) : (
                            properties
                              .filter((p) => {
                                const q = propertiesSearch.toLowerCase()
                                return (
                                  (p.name || "").toLowerCase().includes(q) ||
                                  (p.address || "").toLowerCase().includes(q) ||
                                  (p.managementCompany?.companyName || "").toLowerCase().includes(q)
                                )
                              })
                              .slice(0, propertiesShowCount)
                              .map((p) => (
                                <div key={p._id} className="p-2 border border-gray-200 rounded-md">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h5 className="text-sm font-medium text-gray-900">{p.name}</h5>
                                      {p.locked && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-red-700 border border-red-200 mt-1">
                                          Blocked
                                        </span>
                                      )}
                                      <p className="text-xs text-gray-600">{p.address}</p>
                                      <p className="text-[11px] text-gray-500">
                                        {p.managementCompany?.companyName || "No company"}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handlePropertyView(p)}
                                        className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                                      >
                                        View
                                      </button>
                                      <div className="relative" data-dropdown-container>
                                        <button
                                          onClick={() =>
                                            setPropertyActionsDropdownOpenId(prev =>
                                              prev === p._id ? null : p._id
                                            )
                                          }
                                          className="p-1 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800"
                                          title="Property actions"
                                        >
                                          <span className="sr-only">Property actions</span>
                                          <span className="text-lg leading-none">⋮</span>
                                        </button>
                                        {propertyActionsDropdownOpenId === p._id && (
                                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                            <button
                                              onClick={() => handlePropertyAdminAction(p, 'lock')}
                                              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                            >
                                              {p.locked ? 'Unblock' : 'Block'}
                                            </button>
                                            <button
                                              onClick={() => handlePropertyAdminAction(p, 'remove')}
                                              className="w-full text-left px-3 py-2 text-xs text-red-700 hover:bg-red-50"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                          )}
                        </div>

                        {/* Properties Show More/Less */}
                        <div className="mt-3 text-center space-x-2">
                          {properties.filter((p) => {
                            const q = propertiesSearch.toLowerCase()
                            return (
                              (p.name || "").toLowerCase().includes(q) ||
                              (p.address || "").toLowerCase().includes(q) ||
                              (p.managementCompany?.companyName || "").toLowerCase().includes(q)
                            )
                          }).length > propertiesShowCount && (
                              <button
                                onClick={() => setPropertiesShowCount((prev) => prev + 5)}
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                              >
                                Show More (+5)
                              </button>
                            )}
                          {propertiesShowCount > 5 && (
                            <button
                              onClick={() => setPropertiesShowCount(5)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                            >
                              Show Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Landlords Subsection */}
                  <div className="border border-gray-200 rounded-lg bg-white">
                    <button
                      onClick={() => setLandlordsCollapsed(!landlordsCollapsed)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="person" size="0.875rem" className="text-gray-700 sm:w-4 sm:h-4" />
                        <span className="text-sm font-medium text-gray-900">Landlords</span>
                        <span className="text-xs text-gray-500">({users.filter(u => getDistinctRoles(u).includes('L')).length})</span>
                      </div>
                      <Icon
                        name={landlordsCollapsed ? "chevron-right" : "chevron-down"}
                        size="0.875rem"
                        className="text-gray-600 sm:w-4 sm:h-4 transition-transform duration-200"
                      />
                    </button>

                    {!landlordsCollapsed && (
                      <div className="border-t border-gray-200 p-3">
                        {/* Landlords Search */}
                        <div className="mb-3">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Icon name="search" size="0.875rem" className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search landlords..."
                              value={landlordsSearch}
                              onChange={(e) => setLandlordsSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Landlords List */}
                        <div className="space-y-2">
                          {users
                            .filter(u => getDistinctRoles(u).includes('L'))
                            .filter(u => {
                              const name = (u.name || '').toLowerCase()
                              const email = (u.email || '').toLowerCase()
                              const idNumber = (u.idNumber || '').toLowerCase()
                              const objectId = (u._id ? String(u._id) : '').toLowerCase()
                              const q = landlordsSearch.toLowerCase()
                              return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                            })
                            .slice(0, landlordsShowCount)
                            .map((u) => (
                              <div key={u._id} className="p-2 border border-gray-200 rounded-md">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0 pr-2">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{u.name}</h5>
                                    <p className="text-xs text-gray-600 font-mono truncate">{u.idNumber || String(u._id)}</p>
                                    <p className="text-xs text-gray-600 truncate">{u.email}</p>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      onClick={() => handleActionClick(u._id, 'Wallet')}
                                      className="text-xs bg-emerald-600 text-white px-2.5 py-1.5 rounded-md hover:bg-emerald-700 transition-colors font-medium"
                                      title="Wallet"
                                    >
                                      <Icon name="cash-coin" size="0.875rem" />
                                    </button>
                                    <button
                                      onClick={() => handleActionClick(u._id, 'View Account')}
                                      className="text-xs bg-emerald-600 text-white px-2.5 py-1.5 rounded-md hover:bg-emerald-700 transition-colors font-medium"
                                    >
                                      View
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Landlords Show More/Less */}
                        <div className="mt-3 text-center space-x-2">
                          {users.filter(u => getDistinctRoles(u).includes('L')).filter(u => {
                            const name = (u.name || '').toLowerCase()
                            const email = (u.email || '').toLowerCase()
                            const idNumber = (u.idNumber || '').toLowerCase()
                            const objectId = (u._id ? String(u._id) : '').toLowerCase()
                            const q = landlordsSearch.toLowerCase()
                            return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                          }).length > landlordsShowCount && (
                              <button
                                onClick={() => setLandlordsShowCount(prev => prev + 5)}
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                              >
                                Show More (+5)
                              </button>
                            )}
                          {landlordsShowCount > 5 && (
                            <button
                              onClick={() => setLandlordsShowCount(5)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                            >
                              Show Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tenants Subsection */}
                  <div className="border border-gray-200 rounded-lg bg-white">
                    <button
                      onClick={() => setTenantsCollapsed(!tenantsCollapsed)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="people" size="0.875rem" className="text-emerald-600 sm:w-4 sm:h-4" />
                        <span className="text-sm font-medium text-gray-900">Tenants</span>
                        <span className="text-xs text-gray-500">({users.filter(u => getDistinctRoles(u).includes('T')).length})</span>
                      </div>
                      <Icon
                        name={tenantsCollapsed ? "chevron-right" : "chevron-down"}
                        size="0.875rem"
                        className="text-gray-600 sm:w-4 sm:h-4 transition-transform duration-200"
                      />
                    </button>

                    {!tenantsCollapsed && (
                      <div className="border-t border-gray-200 p-3 sm:p-4">
                        {/* Global Actions Bar */}
                        <div className="mb-4 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm">
                          {/* Header Section */}
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-gray-800">Bulk Actions</h4>
                              <p className="text-xs text-gray-600 hidden sm:block">Trigger payments or send invoices to all tenants</p>
                            </div>
                          </div>

                          {/* Buttons Section - Stack on mobile, side by side on desktop */}
                          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                            <button
                              onClick={handleAdminTriggerAllPayments}
                              disabled={triggerAllPaymentsLoading}
                              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-lg hover:from-gray-800 hover:to-black disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md hover:shadow-lg transition-all duration-200 disabled:hover:shadow-md"
                              title="Trigger payments from all tenant wallets with unpaid rent"
                            >
                              {triggerAllPaymentsLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span>Processing…</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3m6 0a3 3 0 00-3-3m0 0V4m0 7a3 3 0 01-3 3m6 0a3 3 0 01-3-3m0 0v7" />
                                  </svg>
                                  <span>Trigger Payments</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={handleAdminBulkSendInvoices}
                              disabled={bulkTenantInvoiceLoading}
                              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md hover:shadow-lg transition-all duration-200 disabled:hover:shadow-md"
                              title="Send invoices for all active tenants on their assigned units"
                            >
                              {bulkTenantInvoiceLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span>Sending…</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8h18a2 2 0 002-2V8a2 2 0 00-2-2H3a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                  <span>Send Invoices</span>
                                </>
                              )}
                            </button>
                          </div>

                        </div>
                        {/* Tenants Search */}
                        <div className="mb-3">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Icon name="search" size="0.875rem" className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search tenants..."
                              value={tenantsSearch}
                              onChange={(e) => setTenantsSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        {/* Tenants Sort */}
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Sort:</span>
                          <button
                            onClick={() => {
                              if (!tenantsSort || tenantsSort.type !== 'wallet') {
                                setTenantsSort({ type: 'wallet', direction: 'desc' })
                              } else if (tenantsSort.direction === 'desc') {
                                setTenantsSort({ type: 'wallet', direction: 'asc' })
                              } else {
                                setTenantsSort(null)
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all shadow-sm ${tenantsSort?.type === 'wallet'
                              ? 'bg-emerald-600 text-white border border-emerald-700'
                              : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                              }`}
                            title="Toggle wallet sort"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <span>Wallet</span>
                            {tenantsSort?.type === 'wallet' && (
                              <Icon
                                name={tenantsSort.direction === 'desc' ? 'chevron-up' : 'chevron-down'}
                                size="0.75rem"
                                className="text-current"
                              />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              if (!tenantsSort || tenantsSort.type !== 'due') {
                                setTenantsSort({ type: 'due', direction: 'desc' })
                              } else if (tenantsSort.direction === 'desc') {
                                setTenantsSort({ type: 'due', direction: 'asc' })
                              } else {
                                setTenantsSort(null)
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all shadow-sm ${tenantsSort?.type === 'due'
                              ? 'bg-emerald-600 text-white border border-emerald-700'
                              : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                              }`}
                            title="Toggle balance due sort"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <span>Bal Due</span>
                            {tenantsSort?.type === 'due' && (
                              <Icon
                                name={tenantsSort.direction === 'desc' ? 'chevron-up' : 'chevron-down'}
                                size="0.75rem"
                                className="text-current"
                              />
                            )}
                          </button>
                        </div>

                        {/* Tenants List */}
                        <div className="space-y-2">
                          {users
                            .filter(u => getDistinctRoles(u).includes('T'))
                            .filter(u => {
                              const name = (u.name || '').toLowerCase()
                              const email = (u.email || '').toLowerCase()
                              const idNumber = (u.idNumber || '').toLowerCase()
                              const objectId = (u._id ? String(u._id) : '').toLowerCase()
                              const q = tenantsSearch.toLowerCase()
                              return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                            })
                            .sort((a, b) => {
                              if (!tenantsSort) return 0
                              const av = tenantsSort.type === 'wallet'
                                ? Number(userWalletBalancesById[a._id] ?? 0)
                                : Number(userBalanceDueById[a._id] ?? 0)
                              const bv = tenantsSort.type === 'wallet'
                                ? Number(userWalletBalancesById[b._id] ?? 0)
                                : Number(userBalanceDueById[b._id] ?? 0)
                              return tenantsSort.direction === 'asc' ? (av - bv) : (bv - av)
                            })
                            .slice(0, tenantsShowCount)
                            .map((u) => (
                              <div key={u._id} className="p-3 border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow">
                                {/* Header Row: Name + Status Badge */}
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <h5 className="text-sm font-semibold text-gray-900" title={u.name}>{u.name}</h5>
                                    {userActiveTenantById[u._id] === true && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-gray-800 border border-emerald-200 whitespace-nowrap flex-shrink-0">
                                        Active
                                      </span>
                                    )}
                                    {userActiveTenantById[u._id] === false && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-orange-700 border border-orange-200 whitespace-nowrap flex-shrink-0">
                                        Vacated
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                      onClick={() => handleActionClick(u._id, 'Wallet')}
                                      className="text-xs bg-emerald-600 text-white px-2.5 py-1.5 rounded-md hover:bg-emerald-700 transition-colors font-medium whitespace-nowrap"
                                      title="View Wallet"
                                    >
                                      <Icon name="cash-coin" size="0.875rem" />
                                    </button>
                                    <button
                                      onClick={() => handleActionClick(u._id, 'View Account')}
                                      className="text-xs bg-emerald-600 text-white px-2.5 py-1.5 rounded-md hover:bg-emerald-700 transition-colors font-medium whitespace-nowrap"
                                      title="View Account"
                                    >
                                      View
                                    </button>
                                  </div>
                                </div>

                                {/* Info Grid: ID + Email */}
                                <div className="grid grid-cols-1 gap-1 mb-2">
                                  <p className="text-xs text-gray-600 font-mono truncate" title={u.idNumber || String(u._id)}>
                                    <span className="text-gray-400">ID:</span> {u.idNumber || String(u._id)}
                                  </p>
                                  <p className="text-xs text-gray-600 truncate" title={u.email}>
                                    <span className="text-gray-400">Email:</span> {u.email}
                                  </p>
                                </div>

                                {/* Balance Row */}
                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-100">
                                  <div className="flex items-center gap-3 text-[11px] flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <svg className="w-3 h-3 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-gray-500">Wallet:</span>
                                      <span className="font-semibold text-emerald-700">
                                        {userWalletBalancesById[u._id] === undefined ? '—' : formatMoney(Number(userWalletBalancesById[u._id] || 0))}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <svg className="w-3 h-3 text-gray-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-gray-500">Due:</span>
                                      <span className="font-semibold text-red-700">
                                        {formatMoney(Number(userBalanceDueById[u._id] ?? 0))}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Tenants Show More/Less */}
                        <div className="mt-3 text-center space-x-2">
                          {users.filter(u => getDistinctRoles(u).includes('T')).filter(u => {
                            const name = (u.name || '').toLowerCase()
                            const email = (u.email || '').toLowerCase()
                            const idNumber = (u.idNumber || '').toLowerCase()
                            const objectId = (u._id ? String(u._id) : '').toLowerCase()
                            const q = tenantsSearch.toLowerCase()
                            return name.includes(q) || email.includes(q) || idNumber.includes(q) || objectId.includes(q)
                          }).length > tenantsShowCount && (
                              <button
                                onClick={() => setTenantsShowCount(prev => prev + 5)}
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                              >
                                Show More (+5)
                              </button>
                            )}
                          {tenantsShowCount > 5 && (
                            <button
                              onClick={() => setTenantsShowCount(5)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                            >
                              Show Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>

            <div className="mt-3">
              <AdminPayScoreModuleSection onImpersonateUser={startImpersonationForUser} />
            </div>

            <div className="mt-3">
              <AdminUtilitiesModuleSection onImpersonateUser={startImpersonationForUser} />
            </div>

            <div className="mt-3">
              <AdminGroupsModuleSection onImpersonateUser={startImpersonationForUser} />
            </div>
          </div>

          {/* FRP Wallet Transactions Modal */}
          {showRLWalletTransactionsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-0 sm:p-4">
              <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-3 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-2">{walletModalTitle}</h3>
                      <div className="text-sm opacity-90">
                        <span className="font-medium">Current Balance: {formatMoney(walletModalBalance)}</span>
                      </div>
                      <div className="mt-1 text-[11px] sm:text-xs opacity-90">
                        <span className="mr-3">Revenue: {formatMoney(rlPeriodTotals.revenue)}</span>
                        <span>Disbursed: {formatMoney(rlPeriodTotals.disbursed)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowRLWalletTransactionsModal(false)
                        setRLWalletStartDate('')
                        setRLWalletEndDate('')
                        setShowRLWalletDeleteConfirm(false)
                        setRlWalletTxToDelete(null)
                        setRlWalletDeleteError('')
                      }}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Date Filter Section (collapsible) */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setRlWalletFilterOpen(prev => !prev)}
                    className="w-full flex items-center justify-between text-gray-800 font-medium"
                  >
                    <span>Search & Filter</span>
                    <Icon name={rlWalletFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                  </button>
                  {rlWalletFilterOpen && (
                    <div className="mt-3 flex flex-col gap-3">
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Search by AC
                          </label>
                          <input
                            type="text"
                            value={rlWalletAcSearch}
                            onChange={(e) => setRlWalletAcSearch(e.target.value)}
                            placeholder="e.g. 123456789"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Search by TransID
                          </label>
                          <input
                            type="text"
                            value={rlWalletTransIdSearch}
                            onChange={(e) => setRlWalletTransIdSearch(e.target.value)}
                            placeholder="e.g. 123456789"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={rlWalletStartDate}
                            onChange={(e) => setRLWalletStartDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={rlWalletEndDate}
                            onChange={(e) => setRLWalletEndDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                          <button
                            onClick={fetchFilteredRLWalletTransactions}
                            className="flex-1 sm:flex-none bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
                          >
                            Apply Date
                          </button>
                          <button
                            onClick={() => {
                              clearRLWalletDateFilters()
                              setRlWalletAcSearch('')
                              setRlWalletTransIdSearch('')
                            }}
                            className="flex-1 sm:flex-none bg-emerald-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
                          >
                            Clear
                          </button>
                          <button
                            onClick={() => setRlWalletSort(prev => prev === 'desc' ? null : 'desc')}
                            aria-pressed={rlWalletSort === 'desc'}
                            className={`inline-flex items-center justify-center gap-1.5 px-3 sm:px-3.5 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-colors ${rlWalletSort === 'desc' ? 'bg-gray-100 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            title="Sort by highest balance"
                          >
                            <Icon name="chevron-up" size="0.875rem" className={rlWalletSort === 'desc' ? 'text-gray-800' : 'text-gray-500'} />
                            Highest balance
                          </button>
                          <button
                            onClick={() => setRlWalletSort(prev => prev === 'asc' ? null : 'asc')}
                            aria-pressed={rlWalletSort === 'asc'}
                            className={`inline-flex items-center justify-center gap-1.5 px-3 sm:px-3.5 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-colors ${rlWalletSort === 'asc' ? 'bg-gray-100 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            title="Sort by lowest balance"
                          >
                            <Icon name="chevron-down" size="0.875rem" className={rlWalletSort === 'asc' ? 'text-gray-800' : 'text-gray-500'} />
                            Lowest balance
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="p-2 sm:p-6">
                    {rlWalletTransactionsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading transactions...</p>
                      </div>
                    ) : rlWalletTransactions.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm">No transactions found.</p>
                      </div>
                    ) : (
                      <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                        <colgroup>
                          <col className="w-[12%] sm:w-[10%]" />
                          <col className="w-[25%] sm:w-[24%]" />
                          <col className="w-[13%] sm:w-[13%]" />
                          <col className="w-[13%] sm:w-[13%]" />
                          <col className="w-[10%] sm:w-[10%]" />
                          <col className="w-[17%] sm:w-[18%]" />
                          <col className="w-[10%] sm:w-[12%]" />
                        </colgroup>
                        <thead className="bg-emerald-600 text-white">
                          <tr className="text-center">
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">In</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Out</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Fee</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Balance</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rlTransactionsPrepared.map((transaction, index) => {
                            const balanceAfterTransaction = transaction._computedBalance || 0
                            return (
                              <tr key={transaction._id || index} className={`${transaction.synthetic ? 'bg-amber-50' : 'odd:bg-white even:bg-gray-100/40'}`}>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                                  <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top break-words leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>{formatRlTransactionDescription(transaction)}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {(transaction.type === 'platform_fee' || transaction.type === 'payment') && transaction.amount > 0 ? (
                                    <span className="text-gray-800">+{formatMoney(transaction.amount)}</span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {(transaction.type === 'disbursement' || transaction.type === 'disbursement_failed') ? (
                                    <span className="text-red-800">{formatMoney(getRlDisbursementOutAmount(transaction) || 0)}</span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {Number(transaction?.cost || 0) > 0 ? (
                                    <span className="text-gray-800">{formatMoney(Number(transaction.cost))}</span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 font-semibold text-right align-top whitespace-nowrap">
                                  <span className={`${balanceAfterTransaction > 0 ? 'text-gray-800' : balanceAfterTransaction < 0 ? 'text-gray-600' : 'text-gray-800'}`}>
                                    {balanceAfterTransaction < 0 ? `${formatMoney(Math.abs(balanceAfterTransaction))}` : formatMoney(balanceAfterTransaction)}
                                  </span>
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {canEditSplits ? (
                                    <button
                                      onClick={() => openRLWalletDeleteConfirm(transaction)}
                                      disabled={rlWalletTransactionsLoading || rlWalletDeleting}
                                      className="inline-flex items-center justify-center p-1.5 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                      title="Delete transaction"
                                    >
                                      <Icon name="trash" size="0.875rem" className="sm:w-4 sm:h-4" />
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FRP Wallet Delete Confirmation Modal */}
          {showRLWalletDeleteConfirm && rlWalletTxToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80] p-4">
              <div className="bg-white w-full max-w-lg rounded-lg shadow-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">Delete transaction?</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This will permanently remove this transaction from the wallet and adjust the wallet balance.
                  </p>
                </div>
                <div className="px-4 py-3 text-sm text-gray-800 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">Date</span>
                    <span className="font-medium text-right">{formatDateWithTime(rlWalletTxToDelete.timestamp)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">TransID</span>
                    <span className="font-mono text-xs text-right break-all">{String(rlWalletTxToDelete._id)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-semibold text-right">{formatMoney(Number(rlWalletTxToDelete.amount || 0))}</span>
                  </div>
                  <div className="pt-2">
                    <div className="text-gray-600 mb-1">Description</div>
                    <div className="text-gray-900 break-words">{formatRlTransactionDescription(rlWalletTxToDelete)}</div>
                  </div>
                  {rlWalletDeleteError ? (
                    <div className="mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                      {rlWalletDeleteError}
                    </div>
                  ) : null}
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
                  <button
                    onClick={closeRLWalletDeleteConfirm}
                    disabled={rlWalletDeleting}
                    className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteRLWalletTransaction}
                    disabled={rlWalletDeleting}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {rlWalletDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Admin Wallet Transactions Modal */}
          {showAdminWalletModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-0 sm:p-4">
              <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-3 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-2">Admin Wallet</h3>
                      <div className="text-sm opacity-90">
                        <span className="font-medium">Current Balance: {formatMoney(adminWalletBalance)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAdminWalletModal(false)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="p-2 sm:p-6">
                    {adminWalletTransactionsLoading ? (
                      <div className="text-center py-8 text-gray-600">Loading transactions...</div>
                    ) : adminWalletTransactionsPrepared.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No transactions found.</div>
                    ) : (
                      <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                        <colgroup>
                          <col className="w-[22%] sm:w-[18%]" />
                          <col className="w-[48%] sm:w-auto" />
                          <col className="w-[15%] sm:w-[18%]" />
                          <col className="w-[15%] sm:w-[18%]" />
                        </colgroup>
                        <thead className="bg-emerald-600 text-white">
                          <tr className="text-center">
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Amount</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">New Bal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminWalletTransactionsPrepared.map((t, index) => {
                            const balanceAfterTransaction = t._computedBalance || 0
                            return (
                              <tr key={t._id || index} className="odd:bg-white even:bg-emerald-50/40">
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(t.timestamp)}</div>
                                  <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(t.timestamp)}</div>
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top break-words leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>{t.description}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {Number(t.amount || 0) >= 0 ? `+${formatMoney(t.amount)}` : formatMoney(t.amount)}
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap font-semibold">
                                  {formatMoney(balanceAfterTransaction)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* All Wallets Balances Modal */}
          {showAllWalletsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-0 sm:p-4">
              <div className="bg-white w-screen h-screen sm:max-w-3xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-3 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg sm:text-2xl font-bold mb-2">All Wallets Balances</h3>
                      <div className="text-sm opacity-90">Total: {formatMoney(allUsersWalletsSum)}</div>
                    </div>
                    <button
                      onClick={() => setShowAllWalletsModal(false)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Sort Controls */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-700">Sort:</span>
                  <button
                    onClick={() => setAllWalletsSort('desc')}
                    className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium ${allWalletsSort === 'desc' ? 'bg-gray-100 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    aria-pressed={allWalletsSort === 'desc'}
                  >
                    High → Low
                  </button>
                  <button
                    onClick={() => setAllWalletsSort('asc')}
                    className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium ${allWalletsSort === 'asc' ? 'bg-gray-100 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    aria-pressed={allWalletsSort === 'asc'}
                  >
                    Low → High
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="p-2 sm:p-6">
                    {allWalletsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading wallets...</p>
                      </div>
                    ) : sortedAllWallets.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm">No wallets found.</p>
                      </div>
                    ) : (
                      <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[10px] sm:text-xs">
                        <colgroup>
                          <col className="w-[55%] sm:w-[60%]" />
                          <col className="w-[20%] sm:w-[20%]" />
                          <col className="w-[25%] sm:w-[20%]" />
                        </colgroup>
                        <thead className="bg-emerald-600 text-white">
                          <tr className="text-left">
                            <th className="border border-emerald-400/40 px-1 sm:px-2 py-1 sm:py-2 font-semibold">Name</th>
                            <th className="border border-emerald-400/40 px-1 sm:px-2 py-1 sm:py-2 font-semibold">Type</th>
                            <th className="border border-emerald-400/40 px-1 sm:px-2 py-1 sm:py-2 text-right font-semibold">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedAllWallets.map((w) => (
                            <tr key={w.id} className="odd:bg-white even:bg-gray-100/40">
                              <td className="border border-emerald-100 px-1 sm:px-2 py-1 sm:py-2 text-gray-800 align-top break-words">{w.name}</td>
                              <td className="border border-emerald-100 px-1 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-nowrap">{w.type}</td>
                              <td className="border border-emerald-100 px-1 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(w.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showFrpDisburseModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 sm:p-4">
              <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-3 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-base font-bold">Disburse via FRP</h3>
                      <p className="text-xs opacity-90">Select eligible users for FRP-routed disbursement</p>
                    </div>
                    <button
                      onClick={closeFrpDisburseModal}
                      disabled={frpDisburseSubmitting}
                      className="bg-white/20 hover:bg-white/30 rounded-lg p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 flex-shrink-0">
                  <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                    <button
                      onClick={() => {
                        setFrpDisburseMode('single')
                        setFrpDisburseSelectedIds((prev) => prev[0] ? [prev[0]] : (eligibleFrpDisbursementUsers[0]?._id ? [eligibleFrpDisbursementUsers[0]._id] : []))
                      }}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        frpDisburseMode === 'single'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      onClick={() => setFrpDisburseMode('bulk')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        frpDisburseMode === 'bulk'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Bulk
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={frpDisburseSearch}
                        onChange={(e) => setFrpDisburseSearch(e.target.value)}
                        placeholder="Search users..."
                        className="w-full sm:w-64 pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-2 border-b border-gray-200 bg-white flex flex-wrap items-center justify-between gap-2 text-xs flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-600">
                      Eligible: <span className="font-semibold text-emerald-700">{eligibleFrpDisbursementUsers.length}</span>
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-600">
                      Selected: <span className="font-semibold text-emerald-700">{frpDisburseSelectedIds.length}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {frpDisburseMode === 'bulk' && (
                      <>
                        <button
                          onClick={() => setFrpDisburseSelectedIds(filteredFrpDisbursementUsers.map((u) => u._id))}
                          className="text-emerald-600 hover:text-emerald-700 font-medium text-xs"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => setFrpDisburseSelectedIds([])}
                          className="text-gray-500 hover:text-gray-700 text-xs"
                        >
                          Clear
                        </button>
                      </>
                    )}
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!frpDisburseIncludeSystemWallets}
                        onChange={(e) => setFrpDisburseIncludeSystemWallets(e.target.checked)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 w-3.5 h-3.5"
                      />
                      <span className="text-xs text-gray-600">Include system wallets</span>
                    </label>
                  </div>
                </div>

                {frpDisburseSubmitting && (
                  <div className="border-b border-emerald-100 bg-emerald-50 px-4 py-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-emerald-100">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-emerald-900">FRP disbursement is running</p>
                          <p className="text-xs text-emerald-800">
                            The server is validating the selected wallets, sending the disbursement request, and refreshing balances after completion.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-emerald-900 sm:grid-cols-4">
                        <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Mode</div>
                          <div className="mt-1 font-semibold">{frpDisburseMode === 'single' ? 'Single' : 'Bulk'}</div>
                        </div>
                        <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Selected</div>
                          <div className="mt-1 font-semibold">{frpDisburseSelectedIds.length}</div>
                        </div>
                        <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Total</div>
                          <div className="mt-1 font-semibold">{formatMoney(selectedFrpTotal)}</div>
                        </div>
                        <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-emerald-100">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-600">Elapsed</div>
                          <div className="mt-1 font-semibold">{frpDisburseElapsedSeconds}s</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 1: validate wallets</span>
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 2: submit FRP request</span>
                      <span className="rounded-full bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">Step 3: refresh balances</span>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-auto">
                  {frpDisburseBalancesLoading ? (
                    <SkeletonList rows={5} className="p-3" />
                  ) : filteredFrpDisbursementUsers.length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm text-gray-500">No eligible users with wallet balance found</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="border-b border-gray-200">
                          <th className="w-10 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <span className="sr-only">Select</span>
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Details</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredFrpDisbursementUsers.map((oneUser) => {
                          const selected = frpDisburseSelectedIds.includes(oneUser._id)
                          const amount = Number(userWalletBalancesById[oneUser._id] || 0)
                          return (
                            <tr
                              key={oneUser._id}
                              onClick={() => toggleFrpDisburseUser(oneUser._id)}
                              className={`cursor-pointer transition-colors ${
                                selected ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'
                              }`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type={frpDisburseMode === 'single' ? 'radio' : 'checkbox'}
                                  name="frp-disburse-user"
                                  checked={selected}
                                  onChange={() => toggleFrpDisburseUser(oneUser._id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{oneUser.name}</div>
                                <div className="text-xs text-gray-500">{oneUser.phone || 'No phone'}</div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  {oneUser?.role || 'User'}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="text-xs">
                                  <div className="text-gray-700">{oneUser.bankName || '—'}</div>
                                  <div className="text-gray-500 text-[10px]">
                                    {oneUser.bankAccNumber ? `Acc: ${oneUser.bankAccNumber.slice(-4)}` : '—'} | Paybill: {oneUser.paybill || '—'}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className="font-semibold text-emerald-600">
                                  {formatMoney(amount)}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 flex-shrink-0">
                  <div className="text-xs text-gray-500">
                    {frpDisburseSubmitting
                      ? `Processing ${frpDisburseSelectedIds.length} selected wallet(s). Please keep this window open.`
                      : frpDisburseMode === 'single'
                      ? 'Single mode will disburse only the selected user.'
                      : `${frpDisburseSelectedIds.length} user(s) selected for bulk disbursement.`}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={closeFrpDisburseModal}
                      disabled={frpDisburseSubmitting}
                      className="px-4 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitFrpDisbursement}
                      disabled={frpDisburseSubmitting || frpDisburseSelectedIds.length === 0}
                      className="px-4 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {frpDisburseSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                          Disbursing...
                        </span>
                      ) : (
                        `Disburse ${frpDisburseSelectedIds.length > 0 ? `(${frpDisburseSelectedIds.length})` : ''}`
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Company Details Modal */}
          {showCompanyModal && selectedCompany && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 sm:p-4">
              <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden">
                <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Company Details</h3>
                  <button onClick={() => { setShowCompanyModal(false); setSelectedCompany(null) }} className="bg-white/20 hover:bg-white/30 rounded p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="p-4 space-y-2 text-sm">
                  <div><span className="font-medium text-gray-700">Company Name:</span> {selectedCompany.companyName}</div>
                  <div><span className="font-medium text-gray-700">Telephone:</span> {selectedCompany.telephoneNumber}</div>
                  <div><span className="font-medium text-gray-700">Head Office:</span> {selectedCompany.headOfficeLocation}</div>
                  <div><span className="font-medium text-gray-700">Position:</span> {selectedCompany.yourPositionInCompany}</div>
                  <div><span className="font-medium text-gray-700">Owner Names:</span> {selectedCompany.ownerNames}</div>
                  {selectedCompany.user_id && (
                    <div><span className="font-medium text-gray-700">Created By:</span> {selectedCompany.user_id.name || selectedCompany.user_id}</div>
                  )}
                  <div className="text-gray-500 text-xs">Created: {formatTimestamp(selectedCompany.createdAt)}</div>
                  <div className="text-gray-500 text-xs">Updated: {formatTimestamp(selectedCompany.updatedAt)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Property Details Modal */}
          {showPropertyModal && selectedProperty && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 sm:p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full overflow-hidden">
                <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Property Details</h3>
                  <button onClick={() => { setShowPropertyModal(false); setSelectedProperty(null) }} className="bg-white/20 hover:bg-white/30 rounded p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="p-4 space-y-2 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><span className="font-medium text-gray-700">Name:</span> {selectedProperty.name}</div>
                    <div><span className="font-medium text-gray-700">Address:</span> {selectedProperty.address || '-'}</div>
                    <div><span className="font-medium text-gray-700">City/Town:</span> {selectedProperty.cityTown || '-'}</div>
                    <div><span className="font-medium text-gray-700">Location:</span> {selectedProperty.location || '-'}</div>
                    <div><span className="font-medium text-gray-700">Area:</span> {selectedProperty.area || '-'}</div>
                    <div><span className="font-medium text-gray-700">Phone:</span> {selectedProperty.phone || '-'}</div>
                    <div><span className="font-medium text-gray-700">Management %:</span> {selectedProperty.managementPercentage}%</div>
                    <div><span className="font-medium text-gray-700">Manager:</span> {selectedProperty.manager?.name || 'N/A'}</div>
                    <div><span className="font-medium text-gray-700">Company:</span> {selectedProperty.managementCompany?.companyName || 'N/A'}</div>
                    <div><span className="font-medium text-gray-700">Landlord:</span> {selectedProperty.landlord?.name || 'N/A'}</div>
                  </div>
                  {selectedProperty.unitCounts && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700">Units Summary:</span>
                      <div className="text-xs text-gray-600 mt-1">Total: {selectedProperty.unitCounts.total} • Occupied: {selectedProperty.unitCounts.occupied} • Vacant: {selectedProperty.unitCounts.vacant}</div>
                    </div>
                  )}
                  <div className="text-gray-500 text-xs mt-3">Created: {formatTimestamp(selectedProperty.createdAt)}</div>
                  <div className="text-gray-500 text-xs">Updated: {formatTimestamp(selectedProperty.updatedAt)}</div>
                </div>
              </div>
            </div>
          )}

          {/* User Wallet Transactions Modal (Admin) */}
          {showUserWalletTransactionsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-0 sm:p-4">
              <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-3 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg sm:text-2xl font-bold mb-2">{selectedUser?.name || 'User'} Wallet</h3>
                      <div className="text-sm opacity-90">
                        <span className="font-medium">Current Balance: {formatMoney(userWalletBalance)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserWalletTransactionsModal(false)
                        setUserWalletStartDate('')
                        setUserWalletEndDate('')
                        setSelectedUser(null)
                        setUserWalletId(null)
                        setShowUserWalletDeleteConfirm(false)
                        setUserWalletTxToDelete(null)
                        setUserWalletDeleteError('')
                      }}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Date Filter Section (collapsible) */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setUserWalletFilterOpen(prev => !prev)}
                    className="w-full flex items-center justify-between text-gray-800 font-medium"
                  >
                    <span>Filter</span>
                    <Icon name={userWalletFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                  </button>
                  {userWalletFilterOpen && (
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={userWalletStartDate}
                          onChange={(e) => setUserWalletStartDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={userWalletEndDate}
                          onChange={(e) => setUserWalletEndDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600"
                        />
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={fetchFilteredUserWalletTransactions}
                          className="flex-1 sm:flex-none bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
                        >
                          Apply
                        </button>
                        <button
                          onClick={clearUserWalletDateFilters}
                          className="flex-1 sm:flex-none bg-emerald-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="p-2 sm:p-6">
                    {userWalletTransactionsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading transactions...</p>
                      </div>
                    ) : userWalletTransactions.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm">No transactions found.</p>
                      </div>
                    ) : (
                      <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                        <colgroup>
                          <col className="w-[20%] sm:w-[18%]" />
                          <col className="w-[36%] sm:w-auto" />
                          <col className="w-[14%] sm:w-[14%]" />
                          <col className="w-[12%] sm:w-[12%]" />
                          <col className="w-[14%] sm:w-[16%]" />
                          <col className="w-[4%] sm:w-[8%]" />
                        </colgroup>
                        <thead className="bg-emerald-600 text-white">
                          <tr className="text-center">
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Amount</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Fee</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">New Bal</th>
                            <th className="border border-emerald-400/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Del</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userWalletTransactions.map((transaction, index) => {
                            const isOpeningBalanceTransaction = transaction?.type === 'opening_balance'
                            const hasBalanceSnapshot = transaction?.balanceAfter !== null &&
                              transaction?.balanceAfter !== undefined &&
                              Number.isFinite(Number(transaction.balanceAfter))
                            let balanceAfterTransaction = hasBalanceSnapshot ? Number(transaction.balanceAfter) : userWalletBalance || 0
                            if (!hasBalanceSnapshot) {
                              for (let i = 0; i < index; i++) {
                                balanceAfterTransaction -= userWalletTransactions[i].amount
                              }
                            }
                            return (
                              <tr key={transaction._id || index} className="odd:bg-white even:bg-gray-100/40">
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                                  <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top break-words leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                  {(transaction.synthetic || isOpeningBalanceTransaction) ? <span className="font-semibold text-amber-700">{transaction.description}</span> : transaction.description}
                                  {transaction.relatedUnit && (<div className="text-[10px] sm:text-xs text-gray-500 mt-1">Unit: {transaction.relatedUnit.label}</div>)}
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{transaction.amount >= 0 ? `+${formatMoney(transaction.amount)}` : formatMoney(transaction.amount)}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{Number(transaction?.cost || 0) > 0 ? formatMoney(Number(transaction.cost)) : <span className="text-gray-400">—</span>}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 font-semibold text-right align-top whitespace-nowrap">{balanceAfterTransaction < 0 ? `-${formatMoney(Math.abs(balanceAfterTransaction))}` : formatMoney(balanceAfterTransaction)}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                  {canEditSplits && transaction.synthetic ? (
                                    <button
                                      onClick={() => handleEditUserWalletOpeningBalance(transaction)}
                                      disabled={userWalletTransactionsLoading || userWalletDeleting}
                                      className="inline-flex items-center justify-center px-1.5 py-1 rounded-md text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                      title="Edit opening balance"
                                    >
                                      Edit
                                    </button>
                                  ) : canEditSplits && !transaction.synthetic && !isOpeningBalanceTransaction ? (
                                    <button
                                      onClick={() => openUserWalletDeleteConfirm(transaction)}
                                      disabled={userWalletTransactionsLoading || userWalletDeleting}
                                      className="inline-flex items-center justify-center p-1.5 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                      title="Delete transaction"
                                    >
                                      <Icon name="trash" size="0.875rem" className="sm:w-4 sm:h-4" />
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Wallet Delete Confirmation Modal */}
          {showUserWalletDeleteConfirm && userWalletTxToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80] p-4">
              <div className="bg-white w-full max-w-lg rounded-lg shadow-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">Delete transaction?</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This will permanently remove this transaction from the wallet and adjust the wallet balance.
                  </p>
                </div>
                <div className="px-4 py-3 text-sm text-gray-800 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">Date</span>
                    <span className="font-medium text-right">{formatDateWithTime(userWalletTxToDelete.timestamp)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">TransID</span>
                    <span className="font-mono text-xs text-right break-all">{String(userWalletTxToDelete._id)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-semibold text-right">{formatMoney(Number(userWalletTxToDelete.amount || 0))}</span>
                  </div>
                  <div className="pt-2">
                    <div className="text-gray-600 mb-1">Description</div>
                    <div className="text-gray-900 break-words">{String(userWalletTxToDelete.description || '')}</div>
                  </div>
                  {userWalletDeleteError ? (
                    <div className="mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                      {userWalletDeleteError}
                    </div>
                  ) : null}
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
                  <button
                    onClick={closeUserWalletDeleteConfirm}
                    disabled={userWalletDeleting}
                    className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteUserWalletTransaction}
                    disabled={userWalletDeleting}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {userWalletDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Mobile Actions Sheet */}
          {mobileActionsUser && (
            <div className="fixed inset-0 z-[70]">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setMobileActionsUser(null)}
              />
              <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl p-3 border-t border-gray-200">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-2" />
                <div className="text-center mb-2">
                  <div className="text-xs text-gray-500">Actions for</div>
                  <div className="text-sm font-semibold text-gray-900 truncate">{mobileActionsUser.name}</div>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setEditingUser(mobileActionsUser)
                      setMobileActionsUser(null)
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-100 text-emerald-800 text-sm font-medium"
                  >
                    <Icon name="pencil-square" size="1rem" />
                    Edit Profile
                  </button>
                  <button
                    onClick={async () => {
                      const newPinRaw = window.prompt('Enter new 4-digit PIN for this user:')
                      let newPin = (newPinRaw || '').trim()
                      if (!newPin) return
                      newPin = newPin.replace(/\D/g, '')
                      if (newPin.length !== 4) { alert('PIN must be exactly 4 digits'); return }
                      try {
                        const res = await api.put(`/auth/users/${mobileActionsUser._id}/reset-pin`, { newPin })
                        alert(res?.data?.message || 'PIN reset successfully')
                      } catch (e) {
                        alert(e?.response?.data?.error || 'Failed to reset PIN')
                      } finally {
                        setMobileActionsUser(null)
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium"
                  >
                    <Icon name="key" size="1rem" />
                    Change Password
                  </button>
                  {mobileActionsUser.locked ? (
                    <button
                      onClick={async () => {
                        try {
                          const res = await api.put(`/auth/users/${mobileActionsUser._id}/unlock`)
                          alert(res?.data?.message || 'User unblocked')
                          setUsers(prev => prev.map(u => (String(u._id) === String(mobileActionsUser._id) ? { ...u, locked: false } : u)))
                          setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(mobileActionsUser._id) ? { ...u, locked: false } : u)))
                        } catch (e) {
                          alert(e?.response?.data?.error || 'Failed to unblock user')
                        } finally {
                          setMobileActionsUser(null)
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium"
                    >
                      <Icon name="unlock" size="1rem" />
                      Unblock
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Block this user? They will be unable to log in.')) return
                        try {
                          const res = await api.put(`/auth/users/${mobileActionsUser._id}/lock`)
                          alert(res?.data?.message || 'User blocked')
                          setUsers(prev => prev.map(u => (String(u._id) === String(mobileActionsUser._id) ? { ...u, locked: true } : u)))
                          setDisplayedUsers(prev => prev.map(u => (String(u._id) === String(mobileActionsUser._id) ? { ...u, locked: true } : u)))
                        } catch (e) {
                          alert(e?.response?.data?.error || 'Failed to block user')
                        } finally {
                          setMobileActionsUser(null)
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium"
                    >
                      <Icon name="lock" size="1rem" />
                      Block
                    </button>
                  )}
                  <button
                    onClick={() => setMobileActionsUser(null)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-100 text-emerald-800 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <AdminUserProfileModal
            user={editingUser}
            open={!!editingUser}
            onClose={() => setEditingUser(null)}
            onSaved={handleAdminUserProfileSaved}
          />

          {/* Withdrawal Charges Edit Modal */}
          {showWithdrawalChargesModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-0 sm:p-4">
              <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-3 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-2">Edit Withdrawal Charges</h3>
                      <div className="text-sm opacity-90">
                        Configure withdrawal fees by amount ranges
                      </div>
                    </div>
                    <button
                      onClick={() => setShowWithdrawalChargesModal(false)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="p-4 sm:p-6">
                    {withdrawalChargesLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading withdrawal charges...</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 flex justify-between items-center">
                          <h4 className="text-lg font-semibold text-gray-800">Withdrawal Fee Brackets</h4>
                          <button
                            onClick={addWithdrawalChargeRow}
                            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center gap-2"
                          >
                            <Icon name="plus" size="1rem" />
                            Add Row
                          </button>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                  Min Amount
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                  Max Amount
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                  Withdrawal Charge
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {withdrawalCharges.map((charge) => (
                                <tr key={charge.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 border-b border-gray-200">
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={charge.min}
                                      onChange={(e) => updateWithdrawalCharge(charge.id, 'min', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </td>
                                  <td className="px-4 py-3 border-b border-gray-200">
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={charge.max}
                                      onChange={(e) => updateWithdrawalCharge(charge.id, 'max', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </td>
                                  <td className="px-4 py-3 border-b border-gray-200">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={charge.charge}
                                      onChange={(e) => updateWithdrawalCharge(charge.id, 'charge', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </td>
                                  <td className="px-4 py-3 border-b border-gray-200 text-center">
                                    <button
                                      onClick={() => removeWithdrawalChargeRow(charge.id)}
                                      className="text-gray-600 hover:text-red-800 transition-colors p-1"
                                      title="Remove row"
                                    >
                                      <Icon name="trash" size="1rem" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {withdrawalCharges.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p>No withdrawal charges configured.</p>
                            <p className="text-sm">Click "Add Row" to create your first charge bracket.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 flex justify-end gap-3 flex-shrink-0">
                  <button
                    onClick={() => setShowWithdrawalChargesModal(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveWithdrawalCharges}
                    disabled={withdrawalChargesSaving}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {withdrawalChargesSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
    </div>
  )
}

export default AdminDashboardView
